import { useLicense } from '../../hooks/useLicense';
import { LicenseGatedButton } from '../../components/LicenseBlock';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, ArrowLeft, Shield, Network, AlertTriangle, Target, Brain,
  MapPin, Mail, Phone, Globe, Users, Calendar, ChevronRight, Play, Pause,
  Edit2, Trash2, RefreshCw, Loader2, Sparkles, CheckCircle, XCircle,
  Server, Router, Printer, Laptop, HardDrive, Monitor, Smartphone, Wifi,
  Lock, Key, FileText, Download, ExternalLink, Clock, TrendingUp, TrendingDown,
  Crosshair, Zap, Bug, Terminal, FileSignature, Power, PowerOff,
  ChevronDown, ChevronUp, Copy, Eye, EyeOff, BarChart3, PieChart, Activity, KeyRound, Minus,
  Send, FileCheck, FileClock, Share2, Settings, Plus, Route, ArrowRightLeft,
  Cloud, NetworkIcon, Cable, LaptopMinimal
} from 'lucide-react';
import { toast, translateBackendError } from '../../utils/toastUtils';
import { workspaces as workspacesApi, scans as scansApi, schedules as schedulesApi, vpn, contracts as contractsApi, generatedReports, reports, vulnerabilities as vulnApi } from '../../services/api/client';
import { usePageTitle } from '../../hooks/usePageTitle';
import AttackPathDiagram from '../../components/AttackPathDiagram';
import AttackTreeWorkflow from '../../components/AttackTreeWorkflow';
import ContractsTab from './components/ContractsTab';
import ContractStatusDialog from './components/ContractStatusDialog';
import WorkspaceShareDialog from './components/WorkspaceShareDialog';
import { useI18n } from '../../i18n';
import VPNConfigWizard from '../../components/wizards/VPNConfigWizard';
import RiskCartography from '../../components/RiskCartography';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import RegisterAgentModal from '../../components/Agents/RegisterAgentModal';
import DownloadAgentModal from '../../components/Agents/DownloadAgentModal';
import { downloadInstaller, getAgents } from '../../api/agents';

const WorkspaceDetails = () => {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const translateScanType = (scanType) => {
    if (!scanType) return '-';
    const key = `scans.types.${scanType}`;
    const translated = t(key);
    if (translated !== key) return translated;
    const osintKey = `osint.scanTypes.${scanType}`;
    const osintTranslated = t(osintKey);
    if (osintTranslated !== osintKey) return osintTranslated;
    return scanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  usePageTitle('Workspace Details');

  // State
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [networkHosts, setNetworkHosts] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [networkHostsLoading, setNetworkHostsLoading] = useState(false);
  const [vpnStatus, setVpnStatus] = useState(null);
  const [vpnConnecting, setVpnConnecting] = useState(false);
  const [attackPaths, setAttackPaths] = useState([]);
  const [attackTreeData, setAttackTreeData] = useState(null);
  const [attackTreeLoading, setAttackTreeLoading] = useState(false);
  const [showAttackTreeWorkflow, setShowAttackTreeWorkflow] = useState(true);
  const [remediationPlan, setRemediationPlan] = useState(null);
  const [remediationLoading, setRemediationLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    summary: false,
    vpn: false,
    contracts: false,
    business: true,
    scans: false,
    cartography: false,
    network: false,
    vulnerabilities: false,
    attackPaths: false,
    remediation: false,
    riskEvolution: false,
    documents: false
  });
  const [documents, setDocuments] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showVpnWizard, setShowVpnWizard] = useState(false);
  const [exportingScanId, setExportingScanId] = useState(null);
  const [exportingGeoIP, setExportingGeoIP] = useState(false);
  const [aiGeneratedReports, setAiGeneratedReports] = useState([]);
  const [gammaFileReports, setGammaFileReports] = useState([]);
  const [aiReportsLoading, setAiReportsLoading] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [togglingFalsePositive, setTogglingFalsePositive] = useState(null);
  const [showHiddenVulns, setShowHiddenVulns] = useState(false);

  // Business Context state
  const [businessContext, setBusinessContext] = useState(null);
  const [businessContextLoading, setBusinessContextLoading] = useState(false);
  const [businessContextWebsite, setBusinessContextWebsite] = useState('');
  const [businessContextEditing, setBusinessContextEditing] = useState(false);
  const [businessContextError, setBusinessContextError] = useState(null);

  // Utility function to format AI content
  const formatAIContent = (content) => {
    if (!content) return '';

    // Remove <think> tags and their content
    let formatted = content.replace(/<think>[\s\S]*?<\/think>/gi, '');

    // Replace escaped newlines with actual newlines
    formatted = formatted.replace(/\\n\\n/g, '\n\n');
    formatted = formatted.replace(/\\n/g, '\n');

    // Trim whitespace
    formatted = formatted.trim();

    return formatted;
  };

  // Render formatted AI content with markdown-like styling
  const renderAIContent = (content) => {
    if (!content) return null;

    const formatted = formatAIContent(content);

    // Split by lines and process
    const lines = formatted.split('\n');

    return lines.map((line, index) => {
      // Bold text: **text**
      let processedLine = line.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');

      // Italic text: *text*
      processedLine = processedLine.replace(/\*([^*]+)\*/g, '<em>$1</em>');

      // Code: `text`
      processedLine = processedLine.replace(/`([^`]+)`/g, '<code class="bg-gray-800 px-1 rounded text-purple-300">$1</code>');

      // Check if it's a header line (starts with number and dot or has **)
      const isHeader = /^\d+\.\s*\*\*/.test(line) || /^#+\s/.test(line);
      const isBullet = /^\s*[-•]\s/.test(line);

      if (line.trim() === '') {
        return <br key={index} />;
      }

      return (
        <p
          key={index}
          className={`${isHeader ? 'mt-3 mb-1' : isBullet ? 'ml-4' : 'mb-1'}`}
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    });
  };

  // Contract State
  const [latestContract, setLatestContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractSending, setContractSending] = useState(false);
  const [showContractStatusDialog, setShowContractStatusDialog] = useState(false);

  // VPN Credentials Modal State
  const [vpnCredentialsModal, setVpnCredentialsModal] = useState(false);
  const [vpnCredentials, setVpnCredentials] = useState({ username: '', password: '' });
  const [vpnConfigDetails, setVpnConfigDetails] = useState(null);

  // Stored VPN Credentials State
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [storedCredentialsUsername, setStoredCredentialsUsername] = useState('');
  const [loadingStoredCredentials, setLoadingStoredCredentials] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [deletingCredentials, setDeletingCredentials] = useState(false);

  // VPN Routes and Network Info State
  const [vpnRoutes, setVpnRoutes] = useState([]);
  const [vpnRoutesLoading, setVpnRoutesLoading] = useState(false);
  const [showRegisterAgentModal, setShowRegisterAgentModal] = useState(false);
  const [showDownloadAgentModal, setShowDownloadAgentModal] = useState(false);
  const [vpnNetworkInfo, setVpnNetworkInfo] = useState(null);
  const [vpnNetworkInfoLoading, setVpnNetworkInfoLoading] = useState(false);
  const [workspaceAgents, setWorkspaceAgents] = useState([]);
  const [newRoute, setNewRoute] = useState({ destination: '', gateway: '', description: '' });
  const [addingRoute, setAddingRoute] = useState(false);
  const [applyingRoutes, setApplyingRoutes] = useState(false);
  const [routesSectionExpanded, setRoutesSectionExpanded] = useState(false); // Collapsed by default

  // Helper to validate UUID format
  const isValidUUID = (str) => {
    if (!str) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Fetch workspace data
  const fetchWorkspace = useCallback(async () => {
    // Validate that ID is a proper UUID before making API call
    // This prevents 404 errors for reserved paths like "create"
    if (!isValidUUID(id)) {
      console.warn('Invalid workspace ID:', id);
      navigate('/workspaces');
      return;
    }

    try {
      setLoading(true);
      const data = await workspacesApi.get(id);
      setWorkspace(data);
    } catch (error) {
      console.error('Error fetching workspace:', error);
      toast.error(t('common.error'));
      navigate('/workspaces');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch latest contract for this workspace
  const fetchLatestContract = useCallback(async () => {
    if (!id || !isValidUUID(id)) return;
    try {
      setContractLoading(true);
      const contract = await contractsApi.getLatest(id);
      setLatestContract(contract);
    } catch (error) {
      console.error('Error fetching contract:', error);
    } finally {
      setContractLoading(false);
    }
  }, [id]);

  // Send contract for signature
  const handleSendContract = async () => {
    if (!latestContract?.id) return;
    try {
      setContractSending(true);
      await contractsApi.send(latestContract.id);
      toast.success(t('common.save'));
      fetchLatestContract();
    } catch (error) {
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de l\'envoi du contrat');
    } finally {
      setContractSending(false);
    }
  };

  // Fetch workspace details
  const fetchDetails = useCallback(async () => {
    if (!id || !isValidUUID(id)) return;

    try {
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        workspacesApi.getStats(id),
        workspacesApi.getScans(id, 0, 50),
        schedulesApi.list(id).catch(() => []) // Catch 404 errors for schedules
      ]);

      const statsData = results[0].status === 'fulfilled' ? results[0].value : null;
      const scansData = results[1].status === 'fulfilled' ? results[1].value : { scans: [] };
      const schedulesData = results[2].status === 'fulfilled' ? results[2].value : [];

      if (statsData) setStats(statsData);
      const scansArray = Array.isArray(scansData?.scans) ? scansData.scans : [];
      setScans(scansArray);
      setSchedules(Array.isArray(schedulesData) ? schedulesData : []);

      // Compréhension du métier toujours ouverte, tout le reste fermé
      setExpandedSections({
        summary: false,
        vpn: false,
        contracts: false,
        business: true,
        scans: false,
        cartography: false,
        network: false,
        vulnerabilities: false,
        attackPaths: false,
        remediation: false,
        riskEvolution: false,
        documents: false
      });

      // Collect vulnerabilities from scans
      const allVulns = [];
      for (const scan of (scansData.scans || []).filter(s => s.status === 'completed')) {
        try {
          const vulns = await scansApi.getVulnerabilities(scan.id);
          if (vulns && vulns.length > 0) {
            allVulns.push(...vulns.map(v => ({ ...v, scan_id: scan.id, scan_target: scan.target })));
          }
        } catch (e) {
          console.warn(`Could not fetch vulns for scan ${scan.id}`);
        }
      }
      setVulnerabilities(allVulns);

      // Generate attack paths from vulnerabilities
      generateAttackPaths(allVulns);

    } catch (error) {
      console.error('Error fetching workspace details:', error);
    }
  }, [id]);

  // Fetch network hosts
  const fetchNetworkHosts = useCallback(async () => {
    if (!id || !isValidUUID(id)) return;

    try {
      setNetworkHostsLoading(true);
      const scansData = await workspacesApi.getScans(id, 0, 100);
      const completedScans = (scansData.scans || []).filter(s => s.status === 'completed');

      let allHosts = [];
      for (const scan of completedScans) {
        try {
          const hosts = await scansApi.getHosts(scan.id);
          if (hosts && hosts.length > 0) {
            allHosts = [...allHosts, ...hosts];
          }
        } catch (e) {
          console.warn(`Could not fetch hosts for scan ${scan.id}`);
        }
      }

      // Remove duplicates by IP
      const uniqueHosts = allHosts.reduce((acc, host) => {
        const ip = host.ip_address;
        if (!acc.find(h => h.ip_address === ip)) {
          acc.push(host);
        }
        return acc;
      }, []);

      setNetworkHosts(uniqueHosts);
    } catch (error) {
      console.error('Error fetching network hosts:', error);
    } finally {
      setNetworkHostsLoading(false);
    }
  }, [id]);

  // Fetch AI summary
  const fetchAiSummary = useCallback(async (forceRefresh = false) => {
    if (!id || !isValidUUID(id)) return;

    try {
      setAiSummaryLoading(true);
      const data = await workspacesApi.getAiSummary(id, forceRefresh);
      setAiSummary(data);
      if (forceRefresh) {
        toast.success('Résumé IA régénéré avec succès');
      }
    } catch (error) {
      console.error('Error fetching AI summary:', error);
      setAiSummary(null);
      // Always show error toast to inform user
      const errorMessage = translateBackendError(error.response?.data?.detail) || error.message || 'Erreur inconnue';
      toast.error(`Erreur lors de ${forceRefresh ? 'la régénération' : 'du chargement'} du résumé: ${errorMessage}`);
    } finally {
      setAiSummaryLoading(false);
    }
  }, [id]);

  // Fetch complete AI analysis with solutions (silent mode for auto-load)
  const fetchAiAnalysis = useCallback(async (regenerate = false) => {
    if (!id || !isValidUUID(id)) return;

    try {
      setAiAnalysisLoading(true);
      const data = await workspacesApi.getAiAnalysis(id, regenerate);
      setAiAnalysis(data);
      if (regenerate) {
        toast.success('Analyse IA régénérée avec succès');
      }
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      setAiAnalysis(null);
      // Always show error toast to inform user
      const errorMessage = translateBackendError(error.response?.data?.detail) || error.message || 'Erreur inconnue';
      toast.error(`Erreur lors de ${regenerate ? 'la régénération' : 'du chargement'} de l'analyse: ${errorMessage}`);
    } finally {
      setAiAnalysisLoading(false);
    }
  }, [id]);

  // Fetch workspace documents
  const fetchDocuments = useCallback(async () => {
    if (!id || !isValidUUID(id)) return;

    try {
      setDocumentsLoading(true);
      const data = await workspacesApi.getDocuments(id);
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments(null);
    } finally {
      setDocumentsLoading(false);
    }
  }, [id]);

  // Fetch attack tree workflow data
  const fetchAttackTree = useCallback(async () => {
    if (!id || !isValidUUID(id)) return;

    try {
      setAttackTreeLoading(true);
      const data = await workspacesApi.getAttackTree(id);
      setAttackTreeData(data);
    } catch (error) {
      console.error('Error fetching attack tree:', error);
      setAttackTreeData(null);
    } finally {
      setAttackTreeLoading(false);
    }
  }, [id]);

  // Generate attack paths from vulnerabilities
  const generateAttackPaths = (vulns) => {
    if (!vulns || vulns.length === 0) {
      setAttackPaths([]);
      return;
    }

    // Group vulnerabilities by host and severity
    const criticalVulns = vulns.filter(v => v.severity === 'critical' || v.severity === 'high');
    const paths = [];

    // Generate attack chains based on vulnerability types
    criticalVulns.forEach((vuln, index) => {
      const path = {
        id: `path-${index}`,
        name: `Chemin d'attaque ${index + 1}`,
        severity: vuln.severity,
        steps: [
          {
            step: 1,
            action: 'Reconnaissance',
            target: vuln.host || vuln.scan_target,
            technique: 'Port Scanning / Service Enumeration',
            mitre: 'T1046'
          },
          {
            step: 2,
            action: 'Exploitation',
            target: vuln.host || vuln.scan_target,
            vulnerability: vuln.title || vuln.name,
            technique: vuln.cve_id || 'Exploitation de vulnerabilite',
            mitre: 'T1190'
          }
        ],
        impact: vuln.severity === 'critical' ? 'Compromission complete possible' : 'Acces partiel au systeme',
        probability: vuln.severity === 'critical' ? 'Elevee' : 'Moyenne'
      };

      // Add privilege escalation step for RCE vulnerabilities
      if (vuln.title?.toLowerCase().includes('rce') || vuln.title?.toLowerCase().includes('remote code')) {
        path.steps.push({
          step: 3,
          action: 'Elevation de privileges',
          target: vuln.host || vuln.scan_target,
          technique: 'Privilege Escalation',
          mitre: 'T1068'
        });
      }

      // Add lateral movement for network vulnerabilities
      if (vuln.port === 445 || vuln.port === 22 || vuln.port === 3389) {
        path.steps.push({
          step: path.steps.length + 1,
          action: 'Mouvement latéral',
          target: 'Réseau interne',
          technique: 'Lateral Movement via ' + (vuln.port === 445 ? 'SMB' : vuln.port === 22 ? 'SSH' : 'RDP'),
          mitre: 'T1021'
        });
      }

      paths.push(path);
    });

    setAttackPaths(paths.slice(0, 5)); // Limit to 5 attack paths
  };

  // Generate remediation plan
  const generateRemediationPlan = useCallback(async () => {
    if (vulnerabilities.length === 0) {
      toast.info(t('workspaces.noVulnsToFix'));
      return;
    }

    try {
      setRemediationLoading(true);

      // Group vulnerabilities by severity and type
      const grouped = {
        critical: vulnerabilities.filter(v => v.severity === 'critical'),
        high: vulnerabilities.filter(v => v.severity === 'high'),
        medium: vulnerabilities.filter(v => v.severity === 'medium'),
        low: vulnerabilities.filter(v => v.severity === 'low')
      };

      const plan = {
        generated_at: new Date().toISOString(),
        total_vulnerabilities: vulnerabilities.length,
        phases: [
          {
            phase: 1,
            name: 'Actions Immediates',
            priority: 'critique',
            description: 'Corrections urgentes des vulnerabilites critiques',
            actions: grouped.critical.slice(0, 5).map(v => ({
              vulnerability: v.title || v.name,
              host: v.host || v.scan_target,
              action: v.solution || 'Appliquer le correctif de sécurité',
              cve: v.cve_id
            }))
          },
          {
            phase: 2,
            name: 'Corrections Prioritaires',
            priority: 'haute',
            description: 'Remediation des vulnerabilites a risque eleve',
            actions: grouped.high.slice(0, 5).map(v => ({
              vulnerability: v.title || v.name,
              host: v.host || v.scan_target,
              action: v.solution || 'Mettre a jour le composant vulnerable',
              cve: v.cve_id
            }))
          },
          {
            phase: 3,
            name: 'Améliorations de sécurité',
            priority: 'moyenne',
            description: 'Renforcement de la posture de sécurité',
            actions: grouped.medium.slice(0, 5).map(v => ({
              vulnerability: v.title || v.name,
              host: v.host || v.scan_target,
              action: v.solution || 'Configurer selon les bonnes pratiques',
              cve: v.cve_id
            }))
          },
          {
            phase: 4,
            name: 'Hardening',
            priority: 'basse',
            description: 'Durcissement et optimisations',
            actions: grouped.low.slice(0, 5).map(v => ({
              vulnerability: v.title || v.name,
              host: v.host || v.scan_target,
              action: v.solution || 'Optimiser la configuration',
              cve: v.cve_id
            }))
          }
        ].filter(phase => phase.actions.length > 0)
      };

      setRemediationPlan(plan);
      toast.success(t('common.save'));
    } catch (error) {
      console.error('Error generating remediation plan:', error);
      toast.error(t('common.error'));
    } finally {
      setRemediationLoading(false);
    }
  }, [vulnerabilities]);

  // Fetch stored VPN credentials
  const fetchStoredCredentials = useCallback(async () => {
    if (!id) return;

    setLoadingStoredCredentials(true);
    try {
      const response = await vpn.getCredentials(id);
      setHasStoredCredentials(response.has_credentials);
      setStoredCredentialsUsername(response.username || '');

      // Pre-fill username if we have stored credentials
      if (response.has_credentials && response.username) {
        setVpnCredentials(prev => ({ ...prev, username: response.username }));
      }
    } catch (error) {
      console.error('Failed to fetch stored credentials:', error);
      setHasStoredCredentials(false);
      setStoredCredentialsUsername('');
    } finally {
      setLoadingStoredCredentials(false);
    }
  }, [id]);

  // Save VPN credentials securely
  const handleSaveCredentials = async () => {
    if (!workspace?.vpn_config_id) {
      toast.error(t('common.error'));
      return;
    }
    if (!vpnCredentials.username || !vpnCredentials.password) {
      toast.error(t('common.error'));
      return;
    }

    setSavingCredentials(true);
    try {
      await vpn.saveCredentials(
        workspace.vpn_config_id,
        vpnCredentials.username,
        vpnCredentials.password,
        id
      );
      setHasStoredCredentials(true);
      setStoredCredentialsUsername(vpnCredentials.username);
      toast.success(t('common.save'));
      setExpandedSections(prev => ({ ...prev, vpn: false }));
    } catch (error) {
      console.error('Failed to save credentials:', error);
      toast.error('Erreur lors de l\'enregistrement: ' + (translateBackendError(error.response?.data?.detail) || error.message));
    } finally {
      setSavingCredentials(false);
    }
  };

  // Delete stored VPN credentials
  const handleDeleteCredentials = async () => {
    if (!id) return;

    setDeletingCredentials(true);
    try {
      await vpn.deleteCredentials(id);
      setHasStoredCredentials(false);
      setStoredCredentialsUsername('');
      setSaveCredentials(false);
      setVpnCredentials({ username: '', password: '' });
      toast.success(t('common.delete'));
      setExpandedSections(prev => ({ ...prev, vpn: false }));
    } catch (error) {
      console.error('Failed to delete credentials:', error);
      toast.error(t('common.error'));
    } finally {
      setDeletingCredentials(false);
    }
  };

  // Load stored credentials for VPN connection
  const loadStoredCredentialsForConnect = async () => {
    if (!id || !hasStoredCredentials) return null;

    try {
      const response = await vpn.getDecryptedCredentials(id);
      if (response.has_credentials) {
        return {
          username: response.username,
          password: response.password
        };
      }
    } catch (error) {
      console.error('Failed to load stored credentials:', error);
    }
    return null;
  };

  // Fetch VPN routes for this workspace
  const fetchVpnRoutes = useCallback(async () => {
    if (!id) return;
    try {
      setVpnRoutesLoading(true);
      const routes = await vpn.getRoutes(id);
      setVpnRoutes(routes || []);
    } catch (error) {
      console.error('Error fetching VPN routes:', error);
      setVpnRoutes([]);
    } finally {
      setVpnRoutesLoading(false);
    }
  }, [id]);

  // Fetch VPN network info (topology)
  const fetchVpnNetworkInfo = useCallback(async () => {
    if (!id) return;
    try {
      setVpnNetworkInfoLoading(true);
      const info = await vpn.getNetworkInfo(id);
      setVpnNetworkInfo(info);
    } catch (error) {
      console.error('Error fetching VPN network info:', error);
      setVpnNetworkInfo(null);
    } finally {
      setVpnNetworkInfoLoading(false);
    }
  }, [id]);

  const fetchWorkspaceAgents = useCallback(async () => {
    try {
      const agents = await getAgents();
      const relevant = agents.filter(a =>
        a.workspace_id === id || (!a.workspace_id && a.status === 'online')
      );
      setWorkspaceAgents(relevant);
    } catch (error) {
      console.error('Error fetching workspace agents:', error);
    }
  }, [id]);

  // Add a new VPN route
  const handleAddRoute = async () => {
    if (!id || !newRoute.destination || !newRoute.gateway) {
      toast.error('Veuillez renseigner la destination et la passerelle');
      return;
    }
    try {
      setAddingRoute(true);
      await vpn.addRoute(id, newRoute.destination, newRoute.gateway, newRoute.description || null);
      toast.success('Route ajoutee avec succes');
      setNewRoute({ destination: '', gateway: '', description: '' });
      fetchVpnRoutes();
    } catch (error) {
      console.error('Error adding VPN route:', error);
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de l\'ajout de la route');
    } finally {
      setAddingRoute(false);
    }
  };

  // Delete a VPN route
  const handleDeleteRoute = async (routeId) => {
    if (!id || !routeId) return;
    try {
      await vpn.deleteRoute(id, routeId);
      toast.success('Route supprimée');
      fetchVpnRoutes();
    } catch (error) {
      console.error('Error deleting VPN route:', error);
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de la suppression');
    }
  };

  // Apply routes to active VPN connection
  const handleApplyRoutes = async () => {
    if (!id) return;
    try {
      setApplyingRoutes(true);
      await vpn.applyRoutes(id);
      toast.success('Routes appliquees au VPN');
      fetchVpnNetworkInfo();
    } catch (error) {
      console.error('Error applying VPN routes:', error);
      toast.error(translateBackendError(error.response?.data?.detail) || 'Erreur lors de l\'application des routes');
    } finally {
      setApplyingRoutes(false);
    }
  };

  // VPN controls - Check if auth required before connecting
  const handleVpnConnect = async () => {
    if (!workspace?.vpn_config_id) {
      toast.error(t('common.error'));
      return;
    }

    try {
      // Check if VPN requires authentication
      const configs = await vpn.getConfigs(null, workspace.id);
      const allConfigs = [...(configs.interne || []), ...(configs.externe || [])];
      const vpnConfig = allConfigs.find(c => c.id === workspace.vpn_config_id);

      if (vpnConfig?.requires_auth) {
        // If we have stored credentials, use them directly
        if (hasStoredCredentials) {
          const storedCreds = await loadStoredCredentialsForConnect();
          if (storedCreds && storedCreds.username && storedCreds.password) {
            await doVpnConnect(storedCreds.username, storedCreds.password);
            return;
          }
        }

        // Show credentials modal
        setVpnConfigDetails(vpnConfig);
        setVpnCredentialsModal(true);
        return;
      }

      // No auth required, connect directly
      await doVpnConnect(null, null);
    } catch (error) {
      console.error('VPN connect error:', error);
      toast.error('Erreur de connexion VPN: ' + (translateBackendError(error.response?.data?.detail) || error.message));
    }
  };

  // Actually perform VPN connection
  const doVpnConnect = async (username, password) => {
    try {
      setVpnConnecting(true);
      setVpnCredentialsModal(false);

      // Use multi-protocol endpoint for WireGuard and L2TP
      const protocol = workspace.vpn_protocol;
      if (protocol === 'wireguard' || protocol === 'l2tp') {
        const credentials = (username && password) ? { username, password } : null;
        await vpn.connectMultiProtocol(workspace.id, credentials);
      } else {
        // OpenVPN uses the classic endpoint
        await vpn.connect(workspace.vpn_config_id, username, password, workspace.id);
      }
      setVpnStatus({ status: 'connecting', public_ip: t('workspaces.connectionInProgress') });
      toast.success(t('common.loading'));

      // Poll for status
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          // Use appropriate status endpoint based on protocol
          const status = (protocol === 'wireguard' || protocol === 'l2tp')
            ? await vpn.getStatusMultiProtocol(workspace.id)
            : await vpn.getStatus(workspace.id);
          setVpnStatus(status);

          if (status?.status === 'connected') {
            clearInterval(pollInterval);
            setVpnConnecting(false);
            toast.success(`${t('workspaces.vpnConnected')} ${status.public_ip || 'N/A'}`);
            setExpandedSections(prev => ({ ...prev, vpn: false }));
          } else if (status?.status === 'error') {
            clearInterval(pollInterval);
            setVpnConnecting(false);
            toast.error(t('common.error') + ': ' + (status.error_message || t('workspaces.connectionFailed')));
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setVpnConnecting(false);
            toast.error(t('common.error'));
          }
        } catch (e) {
          console.error('VPN status error:', e);
        }
      }, 2000);
    } catch (error) {
      console.error('VPN connect error:', error);
      toast.error('Erreur de connexion VPN: ' + (translateBackendError(error.response?.data?.detail) || error.message));
      setVpnConnecting(false);
    }
  };

  // Handle credentials form submit
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();

    let username = vpnCredentials.username;
    let password = vpnCredentials.password;

    // If fields are empty but we have stored credentials, use them
    if ((!username || !password) && hasStoredCredentials) {
      const storedCreds = await loadStoredCredentialsForConnect();
      if (storedCreds) {
        username = username || storedCreds.username;
        password = password || storedCreds.password;
      }
    }

    if (!username || !password) {
      toast.error(t('common.error'));
      return;
    }

    // Save credentials if checkbox is checked
    if (saveCredentials && vpnCredentials.username && vpnCredentials.password) {
      await handleSaveCredentials();
    }

    doVpnConnect(username, password);
    // Don't clear credentials if we saved them
    if (!saveCredentials && !hasStoredCredentials) {
      setVpnCredentials({ username: '', password: '' });
    }
  };

  const handleVpnDisconnect = async () => {
    try {
      setVpnConnecting(true);
      // Use multi-protocol endpoint for WireGuard and L2TP
      const protocol = workspace.vpn_protocol;
      if (protocol === 'wireguard' || protocol === 'l2tp') {
        await vpn.disconnectMultiProtocol(workspace.id);
      } else {
        await vpn.disconnect(workspace.id);
      }
      setVpnStatus({ status: 'disconnected' });
      toast.success(t('common.save'));
    } catch (error) {
      console.error('VPN disconnect error:', error);
      toast.error(t('common.error'));
    } finally {
      setVpnConnecting(false);
    }
  };

  // Export scan report (PDF or DOCX)
  const handleExportScanReport = async (scanId, format) => {
    setExportingScanId(`${scanId}-${format}`);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/generated-reports/scan/${scanId}/export-passi?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_pentest_${scanId}.${format === 'pdf' ? 'pdf' : 'docx'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Rapport ${format.toUpperCase()} telecharge`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Echec de l'export ${format.toUpperCase()}`);
    } finally {
      setExportingScanId(null);
    }
  };

  // Export GeoIP/Cartography report
  const handleExportGeoIPReport = async () => {
    setExportingGeoIP(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/cartography/export-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: 'Rapport Cartographie des Risques et Conformite',
          workspace_id: id,
          generated_at: new Date().toISOString()
        })
      });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-cartographie-risques-${workspace?.name || id}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Rapport GeoIP telecharge');
    } catch (error) {
      console.error('GeoIP export failed:', error);
      toast.error("Echec de l'export du rapport GeoIP");
    } finally {
      setExportingGeoIP(false);
    }
  };

  // Fetch AI Generated Reports (Gamma) - reports linked to this workspace + file-based reports
  const fetchAiGeneratedReports = useCallback(async () => {
    if (!id) return;
    setAiReportsLoading(true);
    try {
      // Fetch from database (generated_reports table) - filtered by workspace_id
      const dbReports = await generatedReports.list({ workspace_id: id });
      const dbReportsList = Array.isArray(dbReports) ? dbReports : (dbReports?.reports || []);
      setAiGeneratedReports(dbReportsList);

      // Also fetch file-based Gamma reports if no DB reports found
      // Filter by scans of this workspace
      if (dbReportsList.length === 0 && scans.length > 0) {
        try {
          const fileReports = await reports.listGammaReports();
          // Get scan targets and names to match against report filenames
          const scanTargets = scans.map(s => s.target?.toLowerCase()).filter(Boolean);
          const scanNames = scans.map(s => s.name?.toLowerCase()).filter(Boolean);
          const scanIds = scans.map(s => s.id?.toLowerCase()).filter(Boolean);

          // Filter reports that match this workspace's scans
          const workspaceReports = (fileReports?.reports || []).filter(r => {
            const filename = (r.filename || r.name || '').toLowerCase();
            const reportName = (r.name || '').toLowerCase();

            // Check if report matches any scan target, name, or ID
            return scanTargets.some(target => filename.includes(target) || reportName.includes(target)) ||
                   scanNames.some(name => filename.includes(name.replace(/\s+/g, '-')) || filename.includes(name.replace(/\s+/g, '_'))) ||
                   scanIds.some(scanId => filename.includes(scanId));
          });

          setGammaFileReports(workspaceReports);
        } catch (fileError) {
          console.warn('Could not fetch file-based reports:', fileError);
          setGammaFileReports([]);
        }
      } else {
        setGammaFileReports([]);
      }
    } catch (error) {
      console.error('Error fetching AI generated reports:', error);
      setAiGeneratedReports([]);
      setGammaFileReports([]);
    } finally {
      setAiReportsLoading(false);
    }
  }, [id, scans]);

  // Download AI Generated Report
  const handleDownloadAiReport = async (reportId, format) => {
    setDownloadingReportId(`${reportId}-${format}`);
    try {
      const blob = await generatedReports.download(reportId, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-ia-${reportId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Rapport ${format.toUpperCase()} telecharge`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(`Echec du telechargement ${format.toUpperCase()}`);
    } finally {
      setDownloadingReportId(null);
    }
  };

  // Download Gamma File Report (from file system)
  const handleDownloadGammaFile = async (filename) => {
    setDownloadingReportId(`gamma-${filename}`);
    try {
      const blob = await reports.downloadGammaReport(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Rapport telecharge: ${filename}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(`Echec du telechargement: ${filename}`);
    } finally {
      setDownloadingReportId(null);
    }
  };

  // Delete AI Generated Report
  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) return;
    setDeletingReportId(reportId);
    try {
      await generatedReports.delete(reportId);
      setAiGeneratedReports(prev => prev.filter(r => r.id !== reportId));
      toast.success('Rapport supprimé');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Échec de la suppression');
    } finally {
      setDeletingReportId(null);
    }
  };

  // Toggle vulnerability false positive
  const handleToggleFalsePositive = async (vuln, e) => {
    e.stopPropagation(); // Prevent navigation to vulnerability details
    const newValue = !vuln.is_false_positive;
    const reason = newValue ? window.prompt('Raison du faux positif (optionnel):') : null;

    setTogglingFalsePositive(vuln.id);
    try {
      await vulnApi.setFalsePositive(vuln.id, newValue, reason);
      // Update local state
      setVulnerabilities(prev => prev.map(v =>
        v.id === vuln.id
          ? { ...v, is_false_positive: newValue, false_positive_reason: reason }
          : v
      ));
      toast.success(newValue ? 'Marque comme faux positif' : 'Faux positif retire');
    } catch (error) {
      console.error('Toggle false positive failed:', error);
      toast.error('Echec de la mise a jour');
    } finally {
      setTogglingFalsePositive(null);
    }
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Business Context fetch/analyze
  const fetchBusinessContext = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`/api/v1/workspaces/${id}/business-context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const data = await r.json();
        if (data.business_context) {
          setBusinessContext(data.business_context);
          setBusinessContextWebsite(data.website || '');
        } else {
          // No context yet — auto-analyze if workspace has a website URL
          const siteUrl = data.website || workspace?.website;
          if (siteUrl) {
            setBusinessContextWebsite(siteUrl);
            // Trigger auto-analysis in background
            setTimeout(() => autoAnalyzeBusinessContext(siteUrl), 500);
          }
        }
      }
    } catch {}
  };

  const autoAnalyzeBusinessContext = async (websiteUrl) => {
    if (!websiteUrl || !id) return;
    setBusinessContextLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`/api/v1/workspaces/${id}/business-context/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ website_url: websiteUrl })
      });
      if (r.ok) {
        const data = await r.json();
        setBusinessContext(data.business_context);
        setBusinessContextError(null);
      } else {
        const errData = await r.json().catch(() => ({}));
        setBusinessContextError(errData.detail || `Erreur ${r.status} lors de l'analyse automatique`);
      }
    } catch (e) {
      setBusinessContextError("Erreur réseau lors de l'analyse automatique");
    } finally {
      setBusinessContextLoading(false);
    }
  };

  const analyzeBusinessContext = async () => {
    setBusinessContextLoading(true);
    setBusinessContextError(null);
    try {
      const token = localStorage.getItem('access_token');
      const websiteUrl = businessContextWebsite || workspace?.website;
      if (!websiteUrl) {
        setBusinessContextError("Veuillez entrer l'URL du site web de l'entreprise");
        setBusinessContextLoading(false);
        return;
      }
      const r = await fetch(`/api/v1/workspaces/${id}/business-context/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ website_url: websiteUrl })
      });
      if (r.ok) {
        const data = await r.json();
        setBusinessContext(data.business_context);
        setBusinessContextError(null);
      } else {
        const errData = await r.json().catch(() => ({}));
        setBusinessContextError(errData.detail || `Erreur ${r.status} — impossible d'analyser le site`);
      }
    } catch (e) {
      setBusinessContextError("Erreur réseau. Vérifiez la connexion et réessayez.");
    } finally {
      setBusinessContextLoading(false);
    }
  };

  const saveBusinessContextManual = async (updated) => {
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`/api/v1/workspaces/${id}/business-context/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_context: updated })
      });
      setBusinessContext(updated);
    } catch {}
  };

  // Initial load
  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  useEffect(() => {
    if (workspace) {
      fetchDetails();
      fetchNetworkHosts();
      fetchStoredCredentials();
      fetchDocuments();
      fetchLatestContract();
      fetchAiSummary(); // Auto-load AI summary (Résumé IA)
      fetchAiAnalysis();
      fetchAttackTree(); // Auto-load attack tree for workflow view
      fetchWorkspaceAgents();
      fetchBusinessContext();
    }
  }, [workspace, fetchDetails, fetchNetworkHosts, fetchStoredCredentials, fetchDocuments, fetchLatestContract, fetchAiSummary, fetchAiAnalysis, fetchAttackTree, fetchWorkspaceAgents]);

  // Fetch AI/Gamma reports after scans are loaded (depends on scans for filtering)
  useEffect(() => {
    if (workspace && scans.length > 0) {
      fetchAiGeneratedReports();
    }
  }, [workspace, scans, fetchAiGeneratedReports]);

  // Fetch VPN status function - defined at component level for reuse
  const fetchVpnStatus = useCallback(async () => {
    if (!workspace?.id) return;
    try {
      const protocol = workspace.vpn_protocol;
      let status;
      if (protocol === 'wireguard' || protocol === 'l2tp') {
        status = await vpn.getStatusMultiProtocol(workspace.id);
      } else {
        status = await vpn.getStatus(workspace.id);
      }
      setVpnStatus(status);
    } catch (error) {
      // Silently handle 401/403 errors - auth interceptor handles redirect
      if (error.response?.status === 401 || error.response?.status === 403 || error.isAuthRedirect) {
        return;
      }
      // Only log unexpected errors in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error fetching VPN status:', error);
      }
    }
  }, [workspace?.id, workspace?.vpn_protocol]);

  // Poll VPN status when workspace loads
  useEffect(() => {
    if (workspace) {
      fetchVpnStatus();
      // Poll VPN status every 5 seconds
      const interval = setInterval(fetchVpnStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [workspace, fetchVpnStatus]);

  // Fetch VPN routes and network info when workspace loads
  useEffect(() => {
    if (workspace?.id) {
      fetchVpnRoutes();
      fetchVpnNetworkInfo();
    }
  }, [workspace?.id, fetchVpnRoutes, fetchVpnNetworkInfo]);

  // Auto-generate remediation plan when vulnerabilities are loaded
  useEffect(() => {
    if (vulnerabilities.length > 0 && !remediationPlan && !remediationLoading) {
      generateRemediationPlan();
    }
  }, [vulnerabilities, remediationPlan, remediationLoading, generateRemediationPlan]);

  // Network stats
  const networkStats = useMemo(() => {
    if (!networkHosts || networkHosts.length === 0) {
      return { total: 0, vulnerable: 0, subnets: 0, services: 0 };
    }

    const vulnerable = networkHosts.filter(h => (h.vulnerability_count || 0) > 0).length;
    const subnets = new Set(networkHosts.map(h => {
      const ip = h.ip_address || '';
      const parts = ip.split('.');
      return parts.length >= 3 ? `${parts[0]}.${parts[1]}.${parts[2]}.0/24` : ip;
    })).size;
    const services = networkHosts.reduce((acc, h) => acc + (h.open_ports?.length || h.services_count || 0), 0);

    return { total: networkHosts.length, vulnerable, subnets, services };
  }, [networkHosts]);

  // Group hosts by subnet
  const groupedHosts = useMemo(() => {
    const groups = {};
    networkHosts.forEach(host => {
      const ip = host.ip_address || '';
      const parts = ip.split('.');
      const subnet = parts.length >= 3 ? `${parts[0]}.${parts[1]}.${parts[2]}.0/24` : 'Unknown';
      if (!groups[subnet]) groups[subnet] = [];
      groups[subnet].push(host);
    });
    return groups;
  }, [networkHosts]);

  // Vulnerability stats
  const vulnStats = useMemo(() => {
    return {
      critical: vulnerabilities.filter(v => v.severity === 'critical').length,
      high: vulnerabilities.filter(v => v.severity === 'high').length,
      medium: vulnerabilities.filter(v => v.severity === 'medium').length,
      low: vulnerabilities.filter(v => v.severity === 'low').length,
      total: vulnerabilities.length
    };
  }, [vulnerabilities]);

  const onlineAgent = workspaceAgents.find(a => a.status === 'online');
  const hasOnlineAgent = !!onlineAgent;

  // Risk evolution between scans
  const riskEvolution = useMemo(() => {
    if (!scans || scans.length < 2) return null;

    // Get scans with vulnerability data sorted by date
    // Include completed, stopped, and running scans that have vulnerability counts
    const validStatuses = ['completed', 'stopped', 'running'];
    const scansWithData = scans
      .filter(s => validStatuses.includes(s.status) && typeof s.total_vulnerabilities === 'number')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    if (scansWithData.length < 2) return null;

    // Build chart data for all scans (for the stacked area chart)
    // If severity breakdown is not available, use total as "medium" for visualization
    const chartData = scansWithData.slice(0, 10).map(scan => {
      const total = scan.total_vulnerabilities || 0;
      const hasSeverityBreakdown = scan.critical_vulnerabilities != null ||
                                    scan.high_vulnerabilities != null ||
                                    scan.medium_vulnerabilities != null ||
                                    scan.low_vulnerabilities != null;

      return {
        date: scan.created_at,
        dateLabel: new Date(scan.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        // If no breakdown, put all in "total" for single area display
        critical: hasSeverityBreakdown ? (scan.critical_vulnerabilities || 0) : 0,
        high: hasSeverityBreakdown ? (scan.high_vulnerabilities || 0) : 0,
        medium: hasSeverityBreakdown ? (scan.medium_vulnerabilities || 0) : total, // Use total as fallback
        low: hasSeverityBreakdown ? (scan.low_vulnerabilities || 0) : 0,
        total: total,
        scan_name: scan.name || scan.target,
        hasSeverityBreakdown
      };
    });

    // Build comparison data
    const comparisons = [];
    for (let i = 1; i < Math.min(scansWithData.length, 6); i++) {
      const prev = scansWithData[i - 1];
      const curr = scansWithData[i];

      const prevCritical = prev.critical_vulnerabilities || 0;
      const currCritical = curr.critical_vulnerabilities || 0;
      const prevHigh = prev.high_vulnerabilities || 0;
      const currHigh = curr.high_vulnerabilities || 0;
      const prevMedium = prev.medium_vulnerabilities || 0;
      const currMedium = curr.medium_vulnerabilities || 0;
      const prevTotal = prev.total_vulnerabilities || 0;
      const currTotal = curr.total_vulnerabilities || 0;

      comparisons.push({
        scan_id: curr.id,
        scan_name: curr.name || curr.target,
        date: curr.created_at,
        previous_date: prev.created_at,
        critical: { prev: prevCritical, curr: currCritical, diff: currCritical - prevCritical },
        high: { prev: prevHigh, curr: currHigh, diff: currHigh - prevHigh },
        medium: { prev: prevMedium, curr: currMedium, diff: currMedium - prevMedium },
        total: { prev: prevTotal, curr: currTotal, diff: currTotal - prevTotal },
        trend: currTotal < prevTotal ? 'improving' : currTotal > prevTotal ? 'worsening' : 'stable'
      });
    }

    // Calculate overall trend
    const firstScan = scansWithData[0];
    const lastScan = scansWithData[scansWithData.length - 1];
    const overallDiff = (lastScan.total_vulnerabilities || 0) - (firstScan.total_vulnerabilities || 0);

    return {
      chartData,
      comparisons: comparisons.reverse(), // Most recent first
      firstScan,
      lastScan,
      overallDiff,
      overallTrend: overallDiff < 0 ? 'improving' : overallDiff > 0 ? 'worsening' : 'stable',
      improvementPercent: firstScan.total_vulnerabilities > 0
        ? Math.round(((firstScan.total_vulnerabilities - lastScan.total_vulnerabilities) / firstScan.total_vulnerabilities) * 100)
        : 0
    };
  }, [scans]);

  // Device type helper
  const getDeviceType = (host) => {
    const hostname = String(host.hostname || '').toLowerCase();
    const ports = Array.isArray(host.open_ports) ? host.open_ports : [];

    if (hostname.includes('router') || hostname.includes('gateway') || ports.includes(179)) return 'router';
    if (hostname.includes('firewall') || hostname.includes('pfsense')) return 'firewall';
    if (hostname.includes('printer') || ports.includes(9100) || ports.includes(515)) return 'printer';
    if (hostname.includes('nas') || hostname.includes('synology') || ports.includes(5000)) return 'nas';
    if (hostname.includes('server') || ports.includes(22) || ports.includes(3389)) return 'server';
    if (hostname.includes('laptop') || hostname.includes('macbook')) return 'laptop';
    if (ports.includes(80) || ports.includes(443)) return 'web';
    return 'device';
  };

  const DeviceIcon = ({ type, className = "w-4 h-4" }) => {
    const icons = {
      router: Router,
      firewall: Shield,
      printer: Printer,
      nas: HardDrive,
      server: Server,
      laptop: Laptop,
      web: Globe,
      device: Monitor
    };
    const Icon = icons[type] || Monitor;
    return <Icon className={className} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand-green mx-auto mb-4" />
          <p className="text-gray-400">{`${t('common.loading')}`}</p>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Espace de travail introuvable</h2>
          <button onClick={() => navigate('/workspaces')} className="text-brand-green hover:underline">
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/workspaces')}
              className="p-2 hover:bg-brand-black-lighter rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-400 hover:text-brand-green" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                {workspace.logo_base64 ? (
                  <img src={workspace.logo_base64} alt="Logo" className="w-12 h-12 object-contain rounded-lg bg-white p-1" />
                ) : (
                  <Building2 className="w-12 h-12 text-brand-green" />
                )}
                <div>
                  <h1 className="text-3xl font-bold text-white">{workspace.name}</h1>
                  {workspace.company_name && (
                    <p className="text-gray-400">{workspace.company_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3">
                {workspace.city && (
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {workspace.city}
                  </span>
                )}
                <span className={`px-3 py-1 text-xs rounded-full ${
                  workspace.contract_signed
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {workspace.contract_signed ? t('workspaces.contractSigned') : t('workspaces.pendingContract')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShareDialog(true)}
              className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 flex items-center gap-2 transition-colors"
              title={t('workspaces.shareWorkspace')}
            >
              <Share2 className="w-4 h-4" />
              <span className="text-sm font-medium">{`${t('workspaces.shareWorkspace')}`}</span>
            </button>
            <button
              onClick={() => navigate(`/workspaces?edit=${id}`)}
              className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-brand-black-lighter"
              title={t('workspaces.editWorkspace')}
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                fetchDetails();
                fetchNetworkHosts();
              }}
              className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-brand-black-lighter"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Target className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-cyan-400">{stats?.scans_count || 0}</div>
                <div className="text-xs text-gray-500">{`${t('workspaces.scans')}`}</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Network className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400">{networkStats.total}</div>
                <div className="text-xs text-gray-500">{`${t('workspaces.hosts')}`}</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{vulnStats.total}</div>
                <div className="text-xs text-gray-500">{`${t('workspaces.vulnerabilities')}`}</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Crosshair className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-400">{attackPaths.length}</div>
                <div className="text-xs text-gray-500">{`${t('workspaces.attackPaths')}`}</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400">{stats?.successful_executions || 0}</div>
                <div className="text-xs text-gray-500">{`${t('workspaces.successfulScans')}`}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Info + Contact + Share */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Client Info */}
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-3">
            <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand-green" />
              {`${t('workspaces.clientInfo')}`}
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">{`${t('workspaces.company')}`}</span>
                <span className="text-white font-medium truncate ml-2">{workspace.company_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">SIREN</span>
                <span className="text-white font-mono">{workspace.siren || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{`${t('workspaces.legalForm')}`}</span>
                <span className="text-white truncate ml-2">{workspace.legal_form || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{`${t('workspaces.address')}`}</span>
                <span className="text-white truncate ml-2">{workspace.address || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{`${t('workspaces.city')}`}</span>
                <span className="text-white truncate ml-2">{workspace.city ? `${workspace.postal_code} ${workspace.city}` : '-'}</span>
              </div>
              {workspace.website && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{`${t('workspaces.website')}`}</span>
                  <a href={workspace.website} target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline flex items-center gap-1 truncate ml-2">
                    {workspace.website.replace(/^https?:\/\//, '')} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Partager le Workspace */}
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
              <Share2 className="w-4 h-4 text-blue-400" />
              {`${t('workspaces.shareWorkspace')}`}
            </h3>
            <p className="text-gray-400 text-xs mb-2">
              {`${t('workspaces.shareDescription')}`}
            </p>
            <button
              onClick={() => setShowShareDialog(true)}
              disabled={!workspace}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl transition-all text-base font-semibold ${
                workspace
                  ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 shadow-lg shadow-blue-500/20 cursor-pointer'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed opacity-50'
              }`}
            >
              <Share2 className="w-5 h-5" />
              {`${t('workspaces.manageShares')}`}
            </button>
          </div>

          {/* Contact Principal */}
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-400" />
                {`${t('workspaces.mainContact')}`}
              </h3>
              {/* Contract Status Icon */}
              {contractLoading ? (
                <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
              ) : latestContract ? (
                <div className="flex items-center gap-1">
                  {latestContract.status === 'signed' || latestContract.status === 'active' || latestContract.status === 'completed' ? (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-500/20 rounded-full" title={t('workspaces.contractSigned')}>
                      <FileCheck className="w-3 h-3 text-green-400" />
                      <span className="text-xs text-green-400">{`${t('workspaces.signed')}`}</span>
                    </div>
                  ) : latestContract.status === 'pending_signature' ? (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500/20 rounded-full" title={t('workspaces.pending')}>
                      <FileClock className="w-3 h-3 text-yellow-400" />
                      <span className="text-xs text-yellow-400">{`${t('workspaces.pending')}`}</span>
                    </div>
                  ) : latestContract.status === 'draft' ? (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-500/20 rounded-full" title={t('workspaces.draft')}>
                      <FileSignature className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-400">{`${t('workspaces.draft')}`}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/20 rounded-full" title={latestContract.status}>
                      <AlertTriangle className="w-3 h-3 text-red-400" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-700/50 rounded-full" title={t('workspaces.noContract')}>
                  <FileSignature className="w-3 h-3 text-gray-500" />
                </div>
              )}
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-purple-500/20 rounded">
                  <Users className="w-3 h-3 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{workspace.contact_name || '-'}</p>
                  <p className="text-gray-500 text-xs">{workspace.contact_position || t('workspaces.contact')}</p>
                </div>
              </div>
              {workspace.contact_email && (
                <div className="flex items-center gap-2">
                  <div className="p-1 bg-blue-500/20 rounded">
                    <Mail className="w-3 h-3 text-blue-400" />
                  </div>
                  <a href={`mailto:${workspace.contact_email}`} className="text-blue-400 hover:underline truncate">
                    {workspace.contact_email}
                  </a>
                </div>
              )}
              {workspace.contact_phone && (
                <div className="flex items-center gap-2">
                  <div className="p-1 bg-green-500/20 rounded">
                    <Phone className="w-3 h-3 text-green-400" />
                  </div>
                  <a href={`tel:${workspace.contact_phone}`} className="text-green-400 hover:underline">
                    {workspace.contact_phone}
                  </a>
                </div>
              )}

              {/* Contract Action Buttons */}
              {latestContract && (
                <div className="pt-1.5 mt-1.5 border-t border-gray-700">
                  <div className="flex items-center gap-2">
                    {latestContract.status === 'draft' && (
                      <LicenseGatedButton
                        onClick={handleSendContract}
                        disabled={contractSending}
                        feature="la signature de contrats"
                        className="flex items-center gap-1 px-2 py-1 bg-brand-green/20 text-brand-green hover:bg-brand-green/30 rounded transition-colors text-xs font-medium disabled:opacity-50"
                      >
                        {contractSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Envoyer le contrat
                      </LicenseGatedButton>
                    )}
                    {(latestContract.status === 'pending_signature' || latestContract.status === 'signed' || latestContract.status === 'active' || latestContract.status === 'completed') && (
                      <button
                        onClick={() => setShowContractStatusDialog(true)}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded transition-colors text-xs font-medium"
                      >
                        <Eye className="w-3 h-3" />
                        Voir statut
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Business Context Section */}
        <div className="bg-brand-black-light rounded-xl border border-purple-500/30 overflow-hidden">
          <button
            onClick={() => toggleSection('business')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              Compréhension du métier
              {businessContext && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Analysé</span>
              )}
            </h3>
            {expandedSections.business ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.business && (
            <div className="p-6 pt-0 border-t border-gray-800 space-y-5">
              <p className="text-sm text-gray-400 pt-4">
                Analysez le site web de l'entreprise avec notre IA pour comprendre le métier, le secteur et les impacts cyber. Améliore la qualité et la pertinence des rapports de pentest.
              </p>

              {/* Website input + analyze button */}
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Globe className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="url"
                    value={businessContextWebsite || workspace?.website || ''}
                    onChange={e => setBusinessContextWebsite(e.target.value)}
                    placeholder="https://www.entreprise.com"
                    className="w-full pl-9 pr-3 py-2.5 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                <button
                  onClick={analyzeBusinessContext}
                  disabled={businessContextLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  {businessContextLoading
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse...</>
                    : <><Brain className="w-4 h-4" /> Analyser avec IA</>
                  }
                </button>
              </div>

              {/* Auto-analyze loading state */}
              {businessContextLoading && !businessContext && (
                <div className="flex items-center gap-3 py-4 px-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <Loader2 className="w-5 h-5 text-purple-400 animate-spin flex-shrink-0" />
                  <div>
                    <p className="text-sm text-purple-300 font-medium">Analyse IA en cours...</p>
                    <p className="text-xs text-gray-500">L'IA analyse le site web pour comprendre le contexte métier</p>
                  </div>
                </div>
              )}

              {/* Error display */}
              {businessContextError && (
                <div className="flex items-start gap-3 py-3 px-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-300 font-medium">Erreur d'analyse</p>
                    <p className="text-xs text-gray-400 mt-0.5">{businessContextError}</p>
                  </div>
                </div>
              )}

              {/* Results */}
              {businessContext && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Business Description */}
                    <div className="bg-brand-black rounded-xl p-4 border border-purple-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-semibold text-purple-400 uppercase tracking-wide">Métier</span>
                      </div>
                      <p className="text-sm text-white font-medium">
                        {businessContext.sector}{businessContext.sub_sector ? ` — ${businessContext.sub_sector}` : ''}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{businessContext.business_description}</p>
                      {businessContext.company_size && (
                        <p className="text-xs text-gray-500 mt-1">Taille estimée : {businessContext.company_size}</p>
                      )}
                    </div>

                    {/* Cyber Impacts */}
                    <div className="bg-brand-black rounded-xl p-4 border border-red-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-red-400" />
                        <span className="text-xs font-semibold text-red-400 uppercase tracking-wide">Impacts Cyber</span>
                      </div>
                      <p className="text-xs text-gray-300 mb-1">
                        <span className="text-gray-500">Sensibilité données :</span>{' '}
                        <span className={`font-semibold ${
                          businessContext.cyber_impacts?.data_sensitivity === 'critique' ? 'text-red-400' :
                          businessContext.cyber_impacts?.data_sensitivity === 'élevé' ? 'text-orange-400' : 'text-yellow-400'
                        }`}>{businessContext.cyber_impacts?.data_sensitivity || 'N/A'}</span>
                      </p>
                      {businessContext.cyber_impacts?.regulatory_framework?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {businessContext.cyber_impacts.regulatory_framework.map((reg, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">{reg}</span>
                          ))}
                        </div>
                      )}
                      {businessContext.cyber_impacts?.main_risks?.length > 0 && (
                        <ul className="space-y-0.5">
                          {businessContext.cyber_impacts.main_risks.slice(0, 3).map((r, i) => (
                            <li key={i} className="text-xs text-gray-400 flex items-center gap-1">
                              <span className="w-1 h-1 bg-red-400 rounded-full flex-shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Key Services */}
                  {businessContext.key_services?.length > 0 && (
                    <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Services clés</p>
                      <div className="flex flex-wrap gap-2">
                        {businessContext.key_services.map((s, i) => (
                          <span key={i} className="px-2 py-1 bg-brand-black-light border border-gray-700 text-gray-300 text-xs rounded-lg">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pentest Focus */}
                  {businessContext.pentest_focus && (
                    <div className="bg-brand-black rounded-xl p-4 border border-brand-green/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-brand-green" />
                        <span className="text-xs font-semibold text-brand-green uppercase tracking-wide">Focus Pentest adapté au métier</span>
                      </div>
                      <p className="text-sm text-gray-300">{businessContext.pentest_focus}</p>
                    </div>
                  )}

                  {/* Business Continuity + RGPD Fine */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {businessContext.cyber_impacts?.business_continuity_impact && (
                      <div className="bg-brand-black rounded-xl p-3 border border-orange-500/20">
                        <p className="text-xs font-semibold text-orange-400 mb-1">Impact d'une cyberattaque</p>
                        <p className="text-xs text-gray-400">{businessContext.cyber_impacts.business_continuity_impact}</p>
                      </div>
                    )}
                    {businessContext.cyber_impacts?.rgpd_max_fine && (
                      <div className="bg-brand-black rounded-xl p-3 border border-red-500/30">
                        <p className="text-xs font-semibold text-red-400 mb-1">Amende RGPD max. estimée</p>
                        <p className="text-sm font-bold text-red-300">{businessContext.cyber_impacts.rgpd_max_fine}</p>
                        <p className="text-xs text-gray-500 mt-0.5">4% du CA annuel ou 20M€</p>
                      </div>
                    )}
                  </div>

                  {/* SIRENE / Legal Data */}
                  {(businessContext.siren || businessContext.naf_code) && (
                    <div className="bg-brand-black rounded-xl p-3 border border-gray-700 flex flex-wrap gap-4 text-xs">
                      {businessContext.siren && (
                        <div>
                          <span className="text-gray-500">SIREN</span>
                          <p className="text-gray-300 font-mono">{businessContext.siren}</p>
                        </div>
                      )}
                      {businessContext.naf_code && (
                        <div>
                          <span className="text-gray-500">Code NAF/APE</span>
                          <p className="text-gray-300">{businessContext.naf_code}</p>
                        </div>
                      )}
                      {businessContext.company_size && (
                        <div>
                          <span className="text-gray-500">Catégorie</span>
                          <p className="text-gray-300">{businessContext.company_size}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-600 pt-1">
                    <span>Source : {businessContext.website_analyzed || businessContext.website_url}
                      {businessContext.model_used && <span className="ml-2 text-gray-700">• {businessContext.model_used}</span>}
                    </span>
                    <button
                      onClick={analyzeBusinessContext}
                      disabled={businessContextLoading}
                      className="flex items-center gap-1 text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className="w-3 h-3" /> Réanalyser
                    </button>
                  </div>
                </div>
              )}

              {!businessContext && !businessContextLoading && (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-sm">Aucune analyse métier disponible</p>
                  <p className="text-xs mt-1">Entrez l'URL du site web et cliquez sur "Analyser avec IA"</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Contracts Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('contracts')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-green-400" />
              Contrats d'autorisation
              {workspace?.contract_signed && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                  Signé
                </span>
              )}
            </h3>
            {expandedSections.contracts ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.contracts && (
            <div className="p-6 pt-0 border-t border-gray-800">
              <ContractsTab workspace={workspace} onRefresh={fetchWorkspace} />
            </div>
          )}
        </div>

        {/* VPN / Internal Network Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('vpn')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Configuration pour réseau Interne
              {vpnStatus?.status === 'connected' && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">VPN Connecté</span>
              )}
              {workspaceAgents.some(a => a.status === 'online') && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">Agent Connecté</span>
              )}
            </h3>
            {expandedSections.vpn ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.vpn && (
            <div className="p-6 pt-0 border-t border-gray-800 space-y-6">
              {/* Active VPN Connection */}
              {workspace.vpn_config_id ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${vpnStatus?.status === 'connected' ? 'bg-green-500/20' : 'bg-gray-700'}`}>
                        {vpnStatus?.status === 'connected' ? (
                          <Wifi className="w-6 h-6 text-green-400" />
                        ) : (
                          <Lock className="w-6 h-6 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{workspace.vpn_name || 'VPN configuré'}</p>
                          {workspace.vpn_protocol && (
                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                              workspace.vpn_protocol === 'wireguard' ? 'bg-purple-500/20 text-purple-400' :
                              workspace.vpn_protocol === 'l2tp' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {workspace.vpn_protocol === 'openvpn' ? 'OpenVPN' :
                               workspace.vpn_protocol === 'wireguard' ? 'WireGuard' :
                               workspace.vpn_protocol === 'l2tp' ? 'L2TP/IPsec' :
                               workspace.vpn_protocol}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {vpnStatus?.status === 'connected'
                            ? `Connecté - IP : ${vpnStatus.public_ip || 'N/A'}`
                            : 'Non connecté'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={vpnStatus?.status === 'connected' ? handleVpnDisconnect : handleVpnConnect}
                      disabled={vpnConnecting}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
                        vpnStatus?.status === 'connected'
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      }`}
                    >
                      {vpnConnecting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : vpnStatus?.status === 'connected' ? (
                        <>
                          <PowerOff className="w-4 h-4" />
                          Déconnecter
                        </>
                      ) : (
                        <>
                          <Power className="w-4 h-4" />
                          Connecter
                        </>
                      )}
                    </button>
                  </div>

                  {/* Reconfigure VPN button */}
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => setShowVpnWizard(true)}
                      className="text-sm text-gray-400 hover:text-brand-green flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-brand-green/10 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Reconfigurer le VPN
                    </button>
                  </div>

                  {/* Stored credentials indicator */}
                  {loadingStoredCredentials ? (
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verification des identifiants...
                    </div>
                  ) : hasStoredCredentials ? (
                    <div className="flex items-center justify-between p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400">
                          Identifiants VPN enregistres
                        </span>
                        {storedCredentialsUsername && (
                          <span className="text-xs text-gray-400">({storedCredentialsUsername})</span>
                        )}
                      </div>
                      <button
                        onClick={handleDeleteCredentials}
                        disabled={deletingCredentials}
                        className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                      >
                        {deletingCredentials ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-3 h-3" />
                            Supprimer
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                      <Lock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-500">
                        Aucun identifiant VPN enregistré - connectez-vous pour en sauvegarder
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-800/30 rounded-lg border border-gray-700">
                  <Lock className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500">{`${t('workspaces.noVPN')}`}</p>
                  <button
                    onClick={() => setShowVpnWizard(true)}
                    className="mt-3 text-brand-green hover:underline text-sm"
                  >
                    Configurer un VPN
                  </button>
                </div>
              )}

              {/* Network Routes Management */}
              <div className="border-t border-gray-800 pt-6">
                <div
                  className="flex items-center justify-between mb-4 cursor-pointer"
                  onClick={() => setRoutesSectionExpanded(!routesSectionExpanded)}
                >
                  <h4 className="text-md font-semibold text-white flex items-center gap-2">
                    <Route className="w-4 h-4 text-orange-400" />
                    Routes reseau personnalisees
                    {vpnRoutes.length > 0 && (
                      <span className="text-xs text-gray-500">({vpnRoutes.length})</span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${routesSectionExpanded ? 'rotate-180' : ''}`} />
                  </h4>
                  {vpnRoutes.length > 0 && vpnStatus?.status === 'connected' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleApplyRoutes(); }}
                      disabled={applyingRoutes}
                      className="text-sm px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 flex items-center gap-1"
                    >
                      {applyingRoutes ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <ArrowRightLeft className="w-3 h-3" />
                      )}
                      Appliquer les routes
                    </button>
                  )}
                </div>

                {/* Add Route Form */}
                {routesSectionExpanded && (
                  <>
                <div className="bg-brand-black rounded-lg p-4 border border-gray-700 mb-4">
                  <p className="text-xs text-gray-500 mb-3">
                    Ajoutez des routes personnalisees pour acceder aux reseaux internes via le VPN
                    <br />
                    <span className="text-gray-600">Exemple: ip route add 192.168.56.0/24 via 192.168.10.123</span>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Destination (CIDR)</label>
                      <input
                        type="text"
                        placeholder="192.168.56.0/24"
                        value={newRoute.destination}
                        onChange={(e) => setNewRoute({ ...newRoute, destination: e.target.value })}
                        className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Passerelle (Gateway)</label>
                      <input
                        type="text"
                        placeholder="192.168.10.123"
                        value={newRoute.gateway}
                        onChange={(e) => setNewRoute({ ...newRoute, gateway: e.target.value })}
                        className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Description</label>
                      <input
                        type="text"
                        placeholder="Réseau interne client"
                        value={newRoute.description}
                        onChange={(e) => setNewRoute({ ...newRoute, description: e.target.value })}
                        className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleAddRoute}
                        disabled={addingRoute || !newRoute.destination || !newRoute.gateway}
                        className="w-full px-4 py-2 bg-brand-green text-black rounded-lg font-medium hover:bg-brand-green/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {addingRoute ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            Ajouter
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Routes List */}
                {vpnRoutesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : vpnRoutes.length > 0 ? (
                  <div className="space-y-2">
                    {vpnRoutes.map((route) => (
                      <div
                        key={route.id}
                        className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-700"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${route.active ? 'bg-green-500/20' : 'bg-gray-700'}`}>
                            <Route className={`w-4 h-4 ${route.active ? 'text-green-400' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <p className="text-white text-sm font-mono">
                              {route.destination} → {route.gateway}
                            </p>
                            {route.description && (
                              <p className="text-xs text-gray-500">{route.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {route.active && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Active</span>
                          )}
                          <button
                            onClick={() => handleDeleteRoute(route.id)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Aucune route personnalisée configurée
                  </div>
                )}
                </>
                )}
              </div>

              {/* Network Topology Diagram */}
              <div className="border-t border-gray-800 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-white flex items-center gap-2">
                    <Cable className="w-4 h-4 text-cyan-400" />
                    Schema reseau
                  </h4>
                  <button
                    onClick={() => { fetchVpnNetworkInfo(); fetchWorkspaceAgents(); }}
                    disabled={vpnNetworkInfoLoading}
                    className="text-sm text-gray-400 hover:text-brand-green flex items-center gap-1"
                  >
                    {vpnNetworkInfoLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    Actualiser
                  </button>
                </div>

                {/* Network Topology Visual */}
                {hasOnlineAgent ? (
                  /* === Agent Mode Schema === */
                  <div className="bg-gradient-to-r from-brand-black via-gray-900 to-brand-black rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center justify-between gap-4">
                      {/* Server (Sylink) */}
                      <div className="flex flex-col items-center">
                        <div className={`p-4 rounded-xl ${
                          vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green/20 border-brand-green/50' : 'bg-gray-700 border-gray-600'
                        } border`}>
                          <Server className={`w-8 h-8 ${vpnNetworkInfo?.server?.status === 'online' ? 'text-brand-green' : 'text-gray-500'}`} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Serveur Sylink</p>
                        <p className="text-xs text-gray-500">{vpnNetworkInfo?.server?.ip || 'sylink-pentest.fr'}</p>
                        <span className={`mt-1 px-2 py-0.5 rounded text-xs ${
                          vpnNetworkInfo?.server?.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'
                        }`}>
                          {vpnNetworkInfo?.server?.status === 'online' ? 'En ligne' : 'Hors ligne'}
                        </span>
                      </div>

                      {/* Connection Line 1 - WSS */}
                      <div className="flex-1 flex items-center">
                        <div className={`h-0.5 flex-1 ${vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green' : 'bg-gray-600'}`} />
                        <div className={`px-2 py-1 rounded text-xs ${vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-700 text-gray-400'}`}>
                          WSS
                        </div>
                        <div className={`h-0.5 flex-1 ${vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green' : 'bg-gray-600'}`} />
                      </div>

                      {/* Internet */}
                      <div className="flex flex-col items-center">
                        <div className="p-4 rounded-xl bg-blue-500/20 border border-blue-500/50">
                          <Cloud className="w-8 h-8 text-blue-400" />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Internet</p>
                        <p className="text-xs text-gray-500">IP Publique</p>
                        <span className="mt-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                          {vpnNetworkInfo?.public_ip || '-'}
                        </span>
                      </div>

                      {/* Connection Line 2 - Tunnel WSS */}
                      <div className="flex-1 flex items-center">
                        <div className={`h-0.5 flex-1 ${onlineAgent ? 'bg-blue-500' : 'bg-gray-600'}`} />
                        <div className={`px-2 py-1 rounded text-xs ${onlineAgent ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>
                          Tunnel WSS
                        </div>
                        <div className={`h-0.5 flex-1 ${onlineAgent ? 'bg-blue-500' : 'bg-gray-600'}`} />
                      </div>

                      {/* Agent SYLink */}
                      <div className="flex flex-col items-center">
                        <div className={`p-4 rounded-xl ${
                          onlineAgent ? 'bg-blue-500/20 border-blue-500/50' : 'bg-gray-700 border-gray-600'
                        } border`}>
                          <Monitor className={`w-8 h-8 ${onlineAgent ? 'text-blue-400' : 'text-gray-500'}`} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Agent SYLink</p>
                        <p className="text-xs text-gray-500">{onlineAgent?.name || onlineAgent?.hostname || '-'}</p>
                        <p className="text-xs text-gray-500">{onlineAgent?.internal_ip || '-'}</p>
                        <span className={`mt-1 px-2 py-0.5 rounded text-xs ${
                          onlineAgent ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {onlineAgent ? 'Connecté' : 'Déconnecté'}
                        </span>
                      </div>

                      {/* Connection Line 3 - LAN */}
                      <div className="flex-1 flex items-center">
                        <div className={`h-0.5 flex-1 ${onlineAgent ? 'bg-orange-500' : 'bg-gray-600'}`} />
                        <div className={`px-2 py-1 rounded text-xs ${onlineAgent ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-700 text-gray-400'}`}>
                          LAN
                        </div>
                        <div className={`h-0.5 flex-1 ${onlineAgent ? 'bg-orange-500' : 'bg-gray-600'}`} />
                      </div>

                      {/* Client Network */}
                      <div className="flex flex-col items-center">
                        <div className={`p-4 rounded-xl ${
                          onlineAgent ? 'bg-orange-500/20 border-orange-500/50' : 'bg-gray-700 border-gray-600'
                        } border`}>
                          <LaptopMinimal className={`w-8 h-8 ${onlineAgent ? 'text-orange-400' : 'text-gray-500'}`} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Réseau Client</p>
                        <p className="text-xs text-gray-500">Réseau interne</p>
                        <span className={`mt-1 px-2 py-0.5 rounded text-xs ${
                          onlineAgent ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-600 text-gray-400'
                        }`}>
                          {onlineAgent ? 'Accessible' : 'Non accessible'}
                        </span>
                      </div>
                    </div>

                    {/* Agent Info Details */}
                    {onlineAgent && (
                      <div className="mt-6 pt-4 border-t border-gray-700">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">IP Interne</p>
                            <p className="text-sm text-white font-mono">{onlineAgent.internal_ip || '-'}</p>
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">Système</p>
                            <p className="text-sm text-white">{onlineAgent.os_version || '-'}</p>
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">Hostname</p>
                            <p className="text-sm text-white font-mono">{onlineAgent.hostname || '-'}</p>
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">Version</p>
                            <p className="text-sm text-white">{onlineAgent.agent_version || '1.0.0'}</p>
                          </div>
                        </div>
                        {/* CPU & RAM Metrics */}
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="p-3 bg-brand-black rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">CPU</p>
                              <p className="text-xs text-white font-mono">{onlineAgent.cpu_percent != null ? `${onlineAgent.cpu_percent}%` : '-'}</p>
                            </div>
                            {onlineAgent.cpu_percent != null && (
                              <div className="w-full bg-gray-700 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    onlineAgent.cpu_percent > 80 ? 'bg-red-500' : onlineAgent.cpu_percent > 50 ? 'bg-orange-500' : 'bg-brand-green'
                                  }`}
                                  style={{ width: `${Math.min(onlineAgent.cpu_percent, 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">RAM</p>
                              <p className="text-xs text-white font-mono">{onlineAgent.memory_percent != null ? `${onlineAgent.memory_percent}%` : '-'}</p>
                            </div>
                            {onlineAgent.memory_percent != null && (
                              <div className="w-full bg-gray-700 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    onlineAgent.memory_percent > 80 ? 'bg-red-500' : onlineAgent.memory_percent > 50 ? 'bg-orange-500' : 'bg-brand-green'
                                  }`}
                                  style={{ width: `${Math.min(onlineAgent.memory_percent, 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* === VPN Mode Schema (original) === */
                  <div className="bg-gradient-to-r from-brand-black via-gray-900 to-brand-black rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center justify-between gap-4">
                      {/* Server (Sylink) */}
                      <div className="flex flex-col items-center">
                        <div className={`p-4 rounded-xl ${
                          vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green/20 border-brand-green/50' : 'bg-gray-700 border-gray-600'
                        } border`}>
                          <Server className={`w-8 h-8 ${vpnNetworkInfo?.server?.status === 'online' ? 'text-brand-green' : 'text-gray-500'}`} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Serveur Sylink</p>
                        <p className="text-xs text-gray-500">{vpnNetworkInfo?.server?.ip || 'sylink-pentest.fr'}</p>
                        <span className={`mt-1 px-2 py-0.5 rounded text-xs ${
                          vpnNetworkInfo?.server?.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'
                        }`}>
                          {vpnNetworkInfo?.server?.status === 'online' ? 'En ligne' : 'Hors ligne'}
                        </span>
                      </div>

                      {/* Connection Line 1 */}
                      <div className="flex-1 flex items-center">
                        <div className={`h-0.5 flex-1 ${vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green' : 'bg-gray-600'}`} />
                        <div className={`px-2 py-1 rounded text-xs ${vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-700 text-gray-400'}`}>
                          HTTPS
                        </div>
                        <div className={`h-0.5 flex-1 ${vpnNetworkInfo?.server?.status === 'online' ? 'bg-brand-green' : 'bg-gray-600'}`} />
                      </div>

                      {/* Internet */}
                      <div className="flex flex-col items-center">
                        <div className="p-4 rounded-xl bg-blue-500/20 border border-blue-500/50">
                          <Cloud className="w-8 h-8 text-blue-400" />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Internet</p>
                        <p className="text-xs text-gray-500">IP Publique</p>
                        <span className="mt-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                          {vpnNetworkInfo?.public_ip || '-'}
                        </span>
                      </div>

                      {/* Connection Line 2 */}
                      <div className="flex-1 flex items-center">
                        <div className={`h-0.5 flex-1 ${vpnStatus?.status === 'connected' ? 'bg-purple-500' : 'bg-gray-600'}`} />
                        <div className={`px-2 py-1 rounded text-xs ${vpnStatus?.status === 'connected' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-700 text-gray-400'}`}>
                          {workspace?.vpn_protocol?.toUpperCase() || 'VPN'}
                        </div>
                        <div className={`h-0.5 flex-1 ${vpnStatus?.status === 'connected' ? 'bg-purple-500' : 'bg-gray-600'}`} />
                      </div>

                      {/* VPN Gateway */}
                      <div className="flex flex-col items-center">
                        <div className={`p-4 rounded-xl ${
                          vpnStatus?.status === 'connected' ? 'bg-purple-500/20 border-purple-500/50' : 'bg-gray-700 border-gray-600'
                        } border`}>
                          <Shield className={`w-8 h-8 ${vpnStatus?.status === 'connected' ? 'text-purple-400' : 'text-gray-500'}`} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">VPN Gateway</p>
                        <p className="text-xs text-gray-500">{vpnNetworkInfo?.vpn?.server || workspace?.vpn_name || 'Non configuré'}</p>
                        <span className={`mt-1 px-2 py-0.5 rounded text-xs ${
                          vpnStatus?.status === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {vpnStatus?.status === 'connected' ? 'Connecté' : 'Déconnecté'}
                        </span>
                      </div>

                      {/* Connection Line 3 */}
                      <div className="flex-1 flex items-center">
                        <div className={`h-0.5 flex-1 ${vpnStatus?.status === 'connected' ? 'bg-orange-500' : 'bg-gray-600'}`} />
                        <div className={`px-2 py-1 rounded text-xs ${vpnStatus?.status === 'connected' ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-700 text-gray-400'}`}>
                          LAN
                        </div>
                        <div className={`h-0.5 flex-1 ${vpnStatus?.status === 'connected' ? 'bg-orange-500' : 'bg-gray-600'}`} />
                      </div>

                      {/* Client Network */}
                      <div className="flex flex-col items-center">
                        <div className={`p-4 rounded-xl ${
                          vpnStatus?.status === 'connected' ? 'bg-orange-500/20 border-orange-500/50' : 'bg-gray-700 border-gray-600'
                        } border`}>
                          <LaptopMinimal className={`w-8 h-8 ${vpnStatus?.status === 'connected' ? 'text-orange-400' : 'text-gray-500'}`} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-white">Réseau Client</p>
                        <p className="text-xs text-gray-500">
                          {vpnRoutes.length > 0 ? `${vpnRoutes.length} route(s)` : 'Aucune route'}
                        </p>
                        <span className={`mt-1 px-2 py-0.5 rounded text-xs ${
                          vpnStatus?.status === 'connected' && vpnRoutes.length > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-600 text-gray-400'
                        }`}>
                          {vpnStatus?.status === 'connected' ? 'Accessible' : 'Non accessible'}
                        </span>
                      </div>
                    </div>

                    {/* Network Info Details */}
                    {vpnNetworkInfo && (
                      <div className="mt-6 pt-4 border-t border-gray-700">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">IP VPN</p>
                            <p className="text-sm text-white font-mono">{vpnNetworkInfo?.vpn?.assigned_ip || '-'}</p>
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">Protocole</p>
                            <p className="text-sm text-white">{vpnNetworkInfo?.vpn?.protocol || workspace?.vpn_protocol || '-'}</p>
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">Routes actives</p>
                            <p className="text-sm text-white">{vpnRoutes.filter(r => r.active).length} / {vpnRoutes.length}</p>
                          </div>
                          <div className="p-3 bg-brand-black rounded-lg">
                            <p className="text-xs text-gray-500">Latence</p>
                            <p className="text-sm text-white">{vpnNetworkInfo?.latency ? `${vpnNetworkInfo.latency}ms` : '-'}</p>
                          </div>
                        </div>
                        {/* Traffic Statistics Row */}
                        {vpnNetworkInfo?.traffic && (
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="p-3 bg-brand-black rounded-lg flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-green-500/20">
                                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Download</p>
                                <p className="text-sm text-white font-mono">{vpnNetworkInfo.traffic.rx_formatted}</p>
                              </div>
                            </div>
                            <div className="p-3 bg-brand-black rounded-lg flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-blue-500/20">
                                <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Upload</p>
                                <p className="text-sm text-white font-mono">{vpnNetworkInfo.traffic.tx_formatted}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Agent SYLink Sub-section */}
              <div className="mt-6 pt-6 border-t border-gray-800">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-xl bg-blue-500/20">
                    <Server className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white">Agent SYLink</h4>
                    <p className="text-xs text-gray-400">Alternative au VPN pour scans internes</p>
                  </div>
                </div>

                <p className="text-sm text-gray-300 mb-4">
                  Installez notre agent léger sur une machine Windows de votre réseau interne pour scanner sans configurer de VPN.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {[
                    'Installation simple (ZIP ~16 KB)',
                    'Aucune configuration VPN',
                    'Tunnel sécurisé (HTTPS + HMAC)',
                    'Windows 10/11/Server',
                  ].map((feat, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-gray-300">{feat}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mb-4">
                  <button
                    onClick={() => setShowDownloadAgentModal(true)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger l'Agent
                  </button>
                  <button
                    onClick={() => setShowRegisterAgentModal(true)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Enregistrer l'UID
                  </button>
                </div>

                <div className="bg-brand-black rounded-lg p-3 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-lg bg-yellow-500/20">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white mb-0.5">Après installation</p>
                      <p className="text-xs text-gray-400">
                        L'installation générera un <span className="text-brand-green font-mono">UID</span> unique.
                        Allez dans <button onClick={() => navigate('/agents')} className="text-blue-400 hover:underline">Agents</button> pour
                        l'enregistrer et commencer à scanner ce workspace.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* AI Summary Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('summary')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              Résumé IA
              {aiSummary?.has_new_scans && (
                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  Nouveaux scans
                </span>
              )}
            </h3>
            {expandedSections.summary ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.summary && (
            <div className="p-6 pt-0 border-t border-gray-800">
              <div className="flex items-center justify-between mb-4">
                {/* Generation date */}
                {aiSummary?.generated_at && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Généré le {new Date(aiSummary.generated_at).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                    {aiSummary.is_cached && (
                      <span className="ml-2 px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded text-xs">cache</span>
                    )}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {aiSummary?.has_new_scans && (
                    <span className="text-xs text-yellow-400">De nouveaux scans sont disponibles</span>
                  )}
                  <button
                    onClick={() => fetchAiSummary(true)}
                    disabled={aiSummaryLoading}
                    className={`text-sm flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                      aiSummary?.has_new_scans
                        ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                        : 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/10'
                    }`}
                  >
                    {aiSummaryLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Génération...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        {aiSummary ? 'Régénérer' : 'Générer le résumé'}
                      </>
                    )}
                  </button>
                </div>
              </div>

              {aiSummary ? (
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4">
                  <MarkdownRenderer content={aiSummary.summary} variant="compact" />
                  {aiSummary.stats && (
                    <div className="mt-4 pt-4 border-t border-purple-500/20 flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {aiSummary.stats.scans_count} {t('workspaces.scans').toLowerCase()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {aiSummary.stats.hosts_count} {t('workspaces.hosts').toLowerCase()}
                      </span>
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {aiSummary.stats.vulnerabilities_count} {t('workspaces.vulnerabilities').toLowerCase()}
                      </span>
                    </div>
                  )}
                </div>
              ) : !aiSummaryLoading && (
                <div className="bg-brand-black rounded-lg p-6 border border-gray-800 text-center">
                  <Brain className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-500 text-sm">
                    {t('workspaces.clickToGenerateSummary')}
                  </p>
                </div>
              )}

              {/* Complete AI Analysis with Solutions */}
              <div className="mt-6 pt-6 border-t border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    {t('workspaces.completeAnalysis')}
                  </h4>
                  <button
                    onClick={() => fetchAiAnalysis(true)}
                    disabled={aiAnalysisLoading}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-500 hover:to-blue-500 disabled:opacity-50 flex items-center gap-2"
                  >
                    {aiAnalysisLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('workspaces.analysisInProgress')}
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4" />
                        {aiAnalysis ? 'Régénérer l\'analyse' : t('workspaces.launchAiAnalysis')}
                      </>
                    )}
                  </button>
                </div>

                {aiAnalysisLoading && !aiAnalysis && (
                  <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <Brain className="w-16 h-16 text-purple-400" />
                        <Loader2 className="w-8 h-8 text-brand-green absolute -bottom-2 -right-2 animate-spin" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-lg">Analyse en cours...</p>
                        <p className="text-gray-400 text-sm mt-1">Agrégation des données et génération des recommandations</p>
                        <p className="text-gray-500 text-xs mt-2">Cette opération peut prendre quelques instants</p>
                      </div>
                    </div>
                  </div>
                )}

                {!aiAnalysisLoading && !aiAnalysis && (
                  <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-lg p-8">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <Brain className="w-16 h-16 text-gray-500" />
                      <div className="text-center">
                        <p className="text-gray-400 font-medium">{t('workspaces.noAnalysisAvailable')}</p>
                        <p className="text-gray-500 text-sm mt-1">{t('workspaces.clickToLaunchAnalysis')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {aiAnalysis && (
                  <div className="space-y-4">
                    {/* Cache indicator */}
                    {aiAnalysis.from_cache && (
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          Analyse chargée depuis le cache
                        </span>
                        {aiAnalysis.generated_at && (
                          <span>Générée le {new Date(aiAnalysis.generated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        )}
                      </div>
                    )}

                    {/* Data Aggregation Stats */}
                    {aiAnalysis.data_aggregation && (
                      <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                        <h5 className="text-sm font-medium text-gray-400 mb-3 uppercase">{t('workspaces.aggregatedData')}</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="text-center p-2 bg-brand-black-light rounded">
                            <div className="text-2xl font-bold text-brand-green">{aiAnalysis.data_aggregation.stats?.scans_count || 0}</div>
                            <div className="text-xs text-gray-500">{`${t('workspaces.scans')}`}</div>
                          </div>
                          <div className="text-center p-2 bg-brand-black-light rounded">
                            <div className="text-2xl font-bold text-blue-400">{aiAnalysis.data_aggregation.stats?.hosts_count || 0}</div>
                            <div className="text-xs text-gray-500">{t('workspaces.hosts')}</div>
                          </div>
                          <div className="text-center p-2 bg-brand-black-light rounded">
                            <div className="text-2xl font-bold text-red-500">{aiAnalysis.data_aggregation.stats?.critical_count || 0}</div>
                            <div className="text-xs text-gray-500">{t('workspaces.critical')}</div>
                          </div>
                          <div className="text-center p-2 bg-brand-black-light rounded">
                            <div className="text-2xl font-bold text-orange-400">{aiAnalysis.data_aggregation.stats?.high_count || 0}</div>
                            <div className="text-xs text-gray-500">{t('workspaces.high')}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {aiAnalysis.ai_analysis && (
                      <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-medium text-purple-400 uppercase flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            {t('workspaces.riskAnalysis')}
                          </h5>
                          <span className="text-xs text-gray-500 bg-purple-500/20 px-2 py-1 rounded">
                            {aiAnalysis.ai_analysis.model_used}
                          </span>
                        </div>
                        <MarkdownRenderer content={aiAnalysis.ai_analysis.content} variant="compact" />
                      </div>
                    )}

                    {/* AI Solutions */}
                    {aiAnalysis.ai_solutions && (
                      <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-medium text-green-400 uppercase flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {t('workspaces.actionPlan')}
                          </h5>
                          <span className="text-xs text-gray-500 bg-green-500/20 px-2 py-1 rounded">
                            {aiAnalysis.ai_solutions.model_used}
                          </span>
                        </div>
                        <MarkdownRenderer content={aiAnalysis.ai_solutions.content} variant="compact" />
                      </div>
                    )}

                    {/* Top Vulnerabilities */}
                    {aiAnalysis.data_aggregation?.top_vulnerabilities?.length > 0 && (
                      <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                        <h5 className="text-sm font-medium text-gray-400 mb-3 uppercase">Top Vulnerabilites</h5>
                        <div className="space-y-2">
                          {aiAnalysis.data_aggregation.top_vulnerabilities.map((vuln, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-2 bg-brand-black-light rounded text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                                vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {vuln.severity}
                              </span>
                              <span className="text-white flex-1 truncate">{vuln.name}</span>
                              <span className="text-gray-500 text-xs">{vuln.host}:{vuln.port}</span>
                              {vuln.cve && (
                                <span className="text-blue-400 text-xs">{vuln.cve}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Risk Cartography Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden mt-6">
          <button
            onClick={() => toggleSection('cartography')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-400" />
              Cartographie des Risques et Lois
            </h3>
            {expandedSections.cartography ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {expandedSections.cartography && (
            <div className="border-t border-gray-800" style={{ minHeight: '900px' }}>
              <RiskCartography workspaceId={id} compact={false} />
            </div>
          )}
        </div>

        {/* Network Map Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('network')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Network className="w-5 h-5 text-cyan-400" />
              Carte réseau
            </h3>
            {expandedSections.network ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.network && (
            <div className="p-6 pt-0 border-t border-gray-800">
              {/* Network Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                  <div className="text-xl font-bold text-cyan-400">{networkStats.total}</div>
                  <div className="text-xs text-gray-500">{`${t('workspaces.hosts')}`}</div>
                </div>
                <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                  <div className="text-xl font-bold text-red-400">{networkStats.vulnerable}</div>
                  <div className="text-xs text-gray-500">{t('workspaces.vulnerable')}</div>
                </div>
                <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                  <div className="text-xl font-bold text-purple-400">{networkStats.subnets}</div>
                  <div className="text-xs text-gray-500">{t('workspaces.subnets')}</div>
                </div>
                <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                  <div className="text-xl font-bold text-green-400">{networkStats.services}</div>
                  <div className="text-xs text-gray-500">{t('workspaces.services')}</div>
                </div>
              </div>

              {networkHostsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
                </div>
              ) : networkHosts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Network className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('workspaces.noHosts')}</p>
                  <p className="text-xs mt-1">{t('workspaces.launchScanToDiscoverNetwork')}</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {Object.entries(groupedHosts).map(([subnet, hosts]) => (
                    <div key={subnet} className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                      <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 px-4 py-2 border-b border-gray-800">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-cyan-400 text-sm flex items-center gap-2">
                            <Wifi className="w-4 h-4" />
                            {subnet}
                          </span>
                          <span className="text-xs text-gray-500">{hosts.length} hôtes</span>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-800">
                        {hosts.slice(0, 5).map((host, idx) => {
                          const deviceType = getDeviceType(host);
                          const hasVulns = (host.vulnerability_count || 0) > 0;
                          return (
                            <div key={idx} className="px-4 py-2 flex items-center justify-between hover:bg-brand-black-lighter">
                              <div className="flex items-center gap-3">
                                <div className={`p-1.5 rounded ${hasVulns ? 'bg-red-500/20 text-red-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                  <DeviceIcon type={deviceType} className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-mono text-sm text-white">{host.ip_address}</div>
                                  {host.hostname && host.hostname !== host.ip_address && (
                                    <div className="text-xs text-gray-500">{host.hostname}</div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {host.open_ports && host.open_ports.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    {host.open_ports.slice(0, 3).join(', ')}
                                    {host.open_ports.length > 3 && ` +${host.open_ports.length - 3}`}
                                  </span>
                                )}
                                {hasVulns && (
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
                                    {host.vulnerability_count} vulns
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {hosts.length > 5 && (
                          <div className="px-4 py-2 text-center text-xs text-gray-500">
                            + {hosts.length - 5} autres hôtes
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Vulnerabilities Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('vulnerabilities')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Vulnérabilités ({vulnStats.total})
            </h3>
            {expandedSections.vulnerabilities ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.vulnerabilities && (
            <div className="p-6 pt-0 border-t border-gray-800">
              {/* Severity breakdown */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-red-400">{vulnStats.critical}</div>
                  <div className="text-xs text-red-400">Critical</div>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/30 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-orange-400">{vulnStats.high}</div>
                  <div className="text-xs text-orange-400">High</div>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-yellow-400">{vulnStats.medium}</div>
                  <div className="text-xs text-yellow-400">Medium</div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-blue-400">{vulnStats.low}</div>
                  <div className="text-xs text-blue-400">Low</div>
                </div>
              </div>

              {/* Toggle for showing/hiding false positives */}
              {vulnerabilities.some(v => v.is_false_positive) && (
                <div className="flex items-center justify-end mb-3">
                  <button
                    onClick={() => setShowHiddenVulns(!showHiddenVulns)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      showHiddenVulns
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {showHiddenVulns ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {showHiddenVulns ? 'Masquer faux positifs' : `Afficher faux positifs (${vulnerabilities.filter(v => v.is_false_positive).length})`}
                  </button>
                </div>
              )}

              {vulnerabilities.filter(v => showHiddenVulns || !v.is_false_positive).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                  <p>{`${t('workspaces.noVulnerabilities')}`}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {vulnerabilities
                    .filter(v => showHiddenVulns || !v.is_false_positive)
                    .slice(0, 20)
                    .map((vuln, idx) => (
                    <div
                      key={vuln.id || idx}
                      className={`bg-brand-black p-3 rounded-lg border ${
                        vuln.is_false_positive
                          ? 'border-purple-500/30 opacity-60'
                          : 'border-gray-800 hover:border-gray-700'
                      } flex items-center justify-between`}
                    >
                      <div
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => navigate(`/vulnerabilities/${vuln.id}`)}
                      >
                        <span className={`px-2 py-1 text-xs rounded font-medium ${
                          vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                          vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {vuln.severity?.toUpperCase()}
                        </span>
                        <div className="flex-1">
                          <div className="text-white text-sm font-medium flex items-center gap-2">
                            {vuln.title || vuln.name}
                            {vuln.is_false_positive && (
                              <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                                Faux positif
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {vuln.host || vuln.scan_target} {vuln.cve_id && `- ${vuln.cve_id}`}
                            {vuln.false_positive_reason && (
                              <span className="ml-2 text-purple-400">({vuln.false_positive_reason})</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleToggleFalsePositive(vuln, e)}
                          disabled={togglingFalsePositive === vuln.id}
                          className={`p-2 rounded-lg transition-colors ${
                            vuln.is_false_positive
                              ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                              : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                          }`}
                          title={vuln.is_false_positive ? 'Retirer le faux positif' : 'Marquer comme faux positif'}
                        >
                          {togglingFalsePositive === vuln.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : vuln.is_false_positive ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </button>
                        <ChevronRight
                          className="w-4 h-4 text-gray-500 cursor-pointer"
                          onClick={() => navigate(`/vulnerabilities/${vuln.id}`)}
                        />
                      </div>
                    </div>
                  ))}
                  {vulnerabilities.filter(v => showHiddenVulns || !v.is_false_positive).length > 20 && (
                    <div className="text-center text-sm text-gray-500 py-2">
                      + {vulnerabilities.filter(v => showHiddenVulns || !v.is_false_positive).length - 20} autres vulnerabilites
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Attack Paths Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('attackPaths')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Crosshair className="w-5 h-5 text-orange-400" />
              Arbre d'attaque & chemins ({attackPaths.length})
            </h3>
            {expandedSections.attackPaths ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.attackPaths && (
            <div className="p-6 pt-0 border-t border-gray-800">
              {/* Toggle buttons */}
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => {
                    setShowAttackTreeWorkflow(true);
                    if (!attackTreeData) fetchAttackTree();
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    showAttackTreeWorkflow
                      ? 'bg-brand-green text-brand-black'
                      : 'bg-brand-green/20 text-brand-green border border-brand-green/50 hover:bg-brand-green/30'
                  }`}
                >
                  <Network className="w-4 h-4" /> Vue Workflow
                </button>
                <button
                  onClick={() => setShowAttackTreeWorkflow(false)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    !showAttackTreeWorkflow
                      ? 'bg-orange-500 text-white'
                      : 'bg-orange-500/20 text-orange-400 border border-orange-500/50 hover:bg-orange-500/30'
                  }`}
                >
                  <Crosshair className="w-4 h-4" /> Vue Liste
                </button>
              </div>

              {/* Attack Tree Workflow View */}
              {showAttackTreeWorkflow ? (
                <div className="mt-4">
                  {attackTreeLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
                      <span className="ml-3 text-gray-400">Génération de l'arbre d'attaque...</span>
                    </div>
                  ) : attackTreeData && attackTreeData.vulnerabilities?.length > 0 ? (
                    <AttackTreeWorkflow
                      vulnerabilities={attackTreeData.vulnerabilities}
                      workspaceId={id}
                      target={attackTreeData.target}
                      onExecuteExploit={(vuln) => {
                        toast.info(`Lancement du PoC pour ${vuln.cve_id || vuln.title}`);
                        navigate(`/vulnerabilities?workspace=${id}&vuln=${vuln.id}`);
                      }}
                      onSelectVulnerability={(vuln) => {
                        toast.info(`Vulnerabilite selectionnee: ${vuln.title}`);
                      }}
                      compact={false}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Shield className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                      <p>{`${t('workspaces.noAttackTree')}`}</p>
                      <p className="text-xs mt-1">Lancez des scans pour detecter des vulnerabilites</p>
                      <button
                        onClick={fetchAttackTree}
                        className="mt-4 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-all"
                      >
                        <RefreshCw className="w-4 h-4 inline mr-2" />
                        Actualiser
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Attack Paths List View */
                <>
                  {attackPaths.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Shield className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                      <p>{`${t('workspaces.noAttackPaths')}`}</p>
                      <p className="text-xs mt-1">Les vulnérabilités critiques génèrent des chemins d'attaque</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {attackPaths.map((path, idx) => (
                        <div key={path.id} className="bg-brand-black rounded-xl border border-gray-800 overflow-hidden">
                          <div className={`px-4 py-3 border-b border-gray-800 bg-gradient-to-r ${
                            path.severity === 'critical' ? 'from-red-500/10' : 'from-orange-500/10'
                          } to-transparent`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 text-xs rounded font-medium ${
                                  path.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'
                                }`}>
                                  {path.severity?.toUpperCase()}
                                </span>
                                <span className="text-white font-medium">{path.name}</span>
                              </div>
                              <div className="flex items-center gap-4 text-xs">
                                <span className="text-gray-500">Probabilite: <span className="text-orange-400">{path.probability}</span></span>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="flex items-center gap-2 overflow-x-auto pb-2">
                              {path.steps.map((step, stepIdx) => (
                                <React.Fragment key={stepIdx}>
                                  <div className="flex-shrink-0 bg-brand-black-lighter rounded-lg p-3 border border-gray-700 min-w-[200px]">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="w-6 h-6 rounded-full bg-brand-green/20 text-brand-green text-xs flex items-center justify-center">
                                        {step.step}
                                      </span>
                                      <span className="text-white font-medium text-sm">{step.action}</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      <p>Cible: {step.target}</p>
                                      {step.technique && <p>Technique: {step.technique}</p>}
                                      {step.mitre && (
                                        <p className="text-purple-400">MITRE: {step.mitre}</p>
                                      )}
                                    </div>
                                  </div>
                                  {stepIdx < path.steps.length - 1 && (
                                    <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-800">
                              <p className="text-sm text-gray-400">
                                <span className="text-orange-400 font-medium">Impact:</span> {path.impact}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Remediation Plan Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('remediation')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Plan d'action / Remédiation
            </h3>
            {expandedSections.remediation ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.remediation && (
            <div className="p-6 pt-0 border-t border-gray-800">
              <div className="flex justify-end mb-4">
                <button
                  onClick={generateRemediationPlan}
                  disabled={remediationLoading || vulnerabilities.length === 0}
                  className="text-sm text-green-400 hover:text-green-300 flex items-center gap-1 disabled:opacity-50"
                >
                  {remediationLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      {remediationPlan ? 'Régénérer le plan' : 'Générer le plan d\'action'}
                    </>
                  )}
                </button>
              </div>

              {remediationPlan ? (
                <div className="space-y-4">
                  <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">Plan généré le</span>
                      <span className="text-sm text-white">{new Date(remediationPlan.generated_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-gray-400">
                      Total : <span className="text-white font-medium">{remediationPlan.total_vulnerabilities} vulnérabilités</span> à corriger
                    </div>
                  </div>

                  {remediationPlan.phases.map((phase, idx) => (
                    <div key={idx} className={`bg-brand-black rounded-xl border overflow-hidden ${
                      phase.priority === 'critique' ? 'border-red-500/30' :
                      phase.priority === 'haute' ? 'border-orange-500/30' :
                      phase.priority === 'moyenne' ? 'border-yellow-500/30' :
                      'border-blue-500/30'
                    }`}>
                      <div className={`px-4 py-3 border-b ${
                        phase.priority === 'critique' ? 'border-red-500/30 bg-red-500/10' :
                        phase.priority === 'haute' ? 'border-orange-500/30 bg-orange-500/10' :
                        phase.priority === 'moyenne' ? 'border-yellow-500/30 bg-yellow-500/10' :
                        'border-blue-500/30 bg-blue-500/10'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 text-xs rounded font-medium ${
                              phase.priority === 'critique' ? 'bg-red-500/20 text-red-400' :
                              phase.priority === 'haute' ? 'bg-orange-500/20 text-orange-400' :
                              phase.priority === 'moyenne' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              Phase {phase.phase}
                            </span>
                            <span className="text-white font-medium">{phase.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">{phase.actions.length} actions</span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{phase.description}</p>
                      </div>
                      <div className="divide-y divide-gray-800">
                        {phase.actions.map((action, actionIdx) => (
                          <div key={actionIdx} className="px-4 py-3 hover:bg-brand-black-lighter">
                            <div className="flex items-start gap-3">
                              <CheckCircle className="w-4 h-4 text-gray-600 mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-white text-sm font-medium">{action.vulnerability}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Hote: {action.host} {action.cve && `| ${action.cve}`}
                                </p>
                                <p className="text-sm text-green-400 mt-2">{action.action}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !remediationLoading && (
                <div className="bg-brand-black rounded-lg p-6 border border-gray-800 text-center">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-500 text-sm">
                    {vulnerabilities.length === 0
                      ? t('workspaces.noVulnsToFix')
                      : t('workspaces.clickToGenerateActionPlan')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Associated Scans */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('scans')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-brand-green" />
              Scans associés ({scans.length})
            </h3>
            {expandedSections.scans ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.scans && (
          <div className="p-6 pt-0 border-t border-gray-800">
          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('workspaces.noScans')}</p>
              <p className="text-gray-400 mt-1">{t('workspaces.noScansHint')}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {scans.map((scan) => (
                <div
                  key={scan.id}
                  onClick={() => navigate(`/scans/${scan.id}`)}
                  className="bg-brand-black p-3 rounded-lg border border-gray-800 flex items-center justify-between cursor-pointer hover:border-brand-green/50"
                >
                  <div>
                    <div className="font-medium text-white">{scan.name || scan.target}</div>
                    <div className="text-xs text-gray-500">
                      {translateScanType(scan.scan_type)} - {scan.created_at && new Date(scan.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs rounded ${
                      scan.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      scan.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                      scan.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {t(`scans.status.${scan.status}`) || scan.status}
                    </span>
                    {(scan.vulnerabilities_count || scan.total_vulnerabilities) > 0 && (
                      <span className="text-xs text-red-400">
                        {scan.vulnerabilities_count || scan.total_vulnerabilities} vulns
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
          )}
        </div>

        {/* Risk Evolution Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('riskEvolution')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              Évolution des risques
            </h3>
            {expandedSections.riskEvolution ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.riskEvolution && (
            <div className="p-6 pt-0 border-t border-gray-800">
              {!riskEvolution ? (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Minimum 2 scans terminés nécessaires</p>
                  <p className="text-xs mt-1">Lancez des scans réguliers pour suivre l'évolution des risques</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Overall Summary */}
                  <div className={`p-4 rounded-xl border ${
                    riskEvolution.overallTrend === 'improving' ? 'bg-green-500/10 border-green-500/30' :
                    riskEvolution.overallTrend === 'worsening' ? 'bg-red-500/10 border-red-500/30' :
                    'bg-gray-500/10 border-gray-500/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {riskEvolution.overallTrend === 'improving' ? (
                          <div className="p-3 bg-green-500/20 rounded-xl">
                            <TrendingDown className="w-6 h-6 text-green-400" />
                          </div>
                        ) : riskEvolution.overallTrend === 'worsening' ? (
                          <div className="p-3 bg-red-500/20 rounded-xl">
                            <TrendingUp className="w-6 h-6 text-red-400" />
                          </div>
                        ) : (
                          <div className="p-3 bg-gray-500/20 rounded-xl">
                            <Minus className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className={`text-lg font-semibold ${
                            riskEvolution.overallTrend === 'improving' ? 'text-green-400' :
                            riskEvolution.overallTrend === 'worsening' ? 'text-red-400' :
                            'text-gray-400'
                          }`}>
                            {riskEvolution.overallTrend === 'improving' ? 'Amelioration' :
                             riskEvolution.overallTrend === 'worsening' ? 'Degradation' : 'Stable'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Depuis le {new Date(riskEvolution.firstScan.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-3xl font-bold ${
                          riskEvolution.overallDiff < 0 ? 'text-green-400' :
                          riskEvolution.overallDiff > 0 ? 'text-red-400' :
                          'text-gray-400'
                        }`}>
                          {riskEvolution.overallDiff > 0 ? '+' : ''}{riskEvolution.overallDiff}
                        </div>
                        <p className="text-xs text-gray-500">vulnerabilites</p>
                        {riskEvolution.improvementPercent !== 0 && (
                          <p className={`text-sm font-medium ${
                            riskEvolution.improvementPercent > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {riskEvolution.improvementPercent > 0 ? '-' : '+'}{Math.abs(riskEvolution.improvementPercent)}%
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stacked Area Chart */}
                  {riskEvolution.chartData && riskEvolution.chartData.length >= 2 && (
                    <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-gray-400 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-brand-green" />
                          Evolution dans le temps
                        </h4>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-red-500"></span>
                            Critical
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-orange-500"></span>
                            High
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-yellow-500"></span>
                            Medium
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-sm bg-blue-500"></span>
                            Low
                          </span>
                        </div>
                      </div>
                      <div className="relative h-64">
                        <svg className="w-full h-full" viewBox="0 0 600 200" preserveAspectRatio="xMidYMid meet">
                          <defs>
                            <linearGradient id="criticalGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8"/>
                              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.1"/>
                            </linearGradient>
                            <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f97316" stopOpacity="0.8"/>
                              <stop offset="100%" stopColor="#f97316" stopOpacity="0.1"/>
                            </linearGradient>
                            <linearGradient id="mediumGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#eab308" stopOpacity="0.8"/>
                              <stop offset="100%" stopColor="#eab308" stopOpacity="0.1"/>
                            </linearGradient>
                            <linearGradient id="lowGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8"/>
                              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
                            </linearGradient>
                          </defs>

                          {/* Grid lines */}
                          {[0, 50, 100, 150, 200].map((y) => (
                            <line key={y} x1="50" y1={y} x2="580" y2={y} stroke="#374151" strokeDasharray="2,2" strokeWidth="0.5" />
                          ))}

                          {/* Stacked Areas */}
                          {(() => {
                            const data = riskEvolution.chartData;
                            const maxTotal = Math.max(...data.map(d => d.critical + d.high + d.medium + d.low)) || 1;
                            const width = 530;
                            const height = 180;
                            const padding = { left: 50, top: 10 };
                            const xStep = width / (data.length - 1 || 1);

                            const getY = (value) => padding.top + height - (value / maxTotal) * height;

                            // Calculate stacked values
                            const stackedData = data.map((d, i) => {
                              const x = padding.left + i * xStep;
                              return {
                                x,
                                low: d.low,
                                medium: d.low + d.medium,
                                high: d.low + d.medium + d.high,
                                critical: d.low + d.medium + d.high + d.critical,
                                date: d.dateLabel,
                                total: d.total
                              };
                            });

                            // Create area paths (from bottom to top: low, medium, high, critical)
                            const createAreaPath = (dataKey, prevKey = null) => {
                              const topLine = stackedData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${d.x} ${getY(d[dataKey])}`).join(' ');
                              const bottomLine = prevKey
                                ? [...stackedData].reverse().map((d, i) => `${i === 0 ? 'L' : 'L'} ${d.x} ${getY(d[prevKey])}`).join(' ')
                                : [...stackedData].reverse().map((d, i) => `${i === 0 ? 'L' : 'L'} ${d.x} ${getY(0)}`).join(' ');
                              return `${topLine} ${bottomLine} Z`;
                            };

                            return (
                              <>
                                {/* Low area (bottom) */}
                                <path d={createAreaPath('low')} fill="url(#lowGrad)" />
                                {/* Medium area */}
                                <path d={createAreaPath('medium', 'low')} fill="url(#mediumGrad)" />
                                {/* High area */}
                                <path d={createAreaPath('high', 'medium')} fill="url(#highGrad)" />
                                {/* Critical area (top) */}
                                <path d={createAreaPath('critical', 'high')} fill="url(#criticalGrad)" />

                                {/* Data points */}
                                {stackedData.map((d, i) => (
                                  <g key={i}>
                                    <circle cx={d.x} cy={getY(d.critical)} r="4" fill="#ef4444" className="opacity-80" />
                                    <circle cx={d.x} cy={getY(d.critical)} r="6" fill="#ef4444" className="opacity-20" />
                                  </g>
                                ))}

                                {/* X-axis labels */}
                                {stackedData.map((d, i) => (
                                  <text key={`label-${i}`} x={d.x} y="198" fill="#6b7280" fontSize="10" textAnchor="middle">
                                    {d.date}
                                  </text>
                                ))}

                                {/* Y-axis labels */}
                                {[0, Math.round(maxTotal/2), maxTotal].map((v, i) => (
                                  <text key={`y-${i}`} x="45" y={getY(v) + 4} fill="#6b7280" fontSize="10" textAnchor="end">
                                    {v}
                                  </text>
                                ))}
                              </>
                            );
                          })()}
                        </svg>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2 px-4">
                        <span>Premier scan: {new Date(riskEvolution.firstScan.created_at).toLocaleDateString('fr-FR')}</span>
                        <span>Dernier scan: {new Date(riskEvolution.lastScan.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  )}

                  {/* Comparison Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Scan</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Date</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-red-400">Critical</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-orange-400">High</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-yellow-400">Medium</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Total</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Evolution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskEvolution.comparisons.map((comp, idx) => (
                          <tr
                            key={comp.scan_id}
                            onClick={() => navigate(`/scans/${comp.scan_id}`)}
                            className="border-b border-gray-800 hover:bg-brand-black/50 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              <div className="text-sm text-white font-medium truncate max-w-[200px]">{comp.scan_name}</div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className="text-xs text-gray-500">
                                {new Date(comp.date).toLocaleDateString('fr-FR')}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-sm text-white">{comp.critical.curr}</span>
                                {comp.critical.diff !== 0 && (
                                  <span className={`text-xs ${comp.critical.diff > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    ({comp.critical.diff > 0 ? '+' : ''}{comp.critical.diff})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-sm text-white">{comp.high.curr}</span>
                                {comp.high.diff !== 0 && (
                                  <span className={`text-xs ${comp.high.diff > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    ({comp.high.diff > 0 ? '+' : ''}{comp.high.diff})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-sm text-white">{comp.medium.curr}</span>
                                {comp.medium.diff !== 0 && (
                                  <span className={`text-xs ${comp.medium.diff > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    ({comp.medium.diff > 0 ? '+' : ''}{comp.medium.diff})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-sm text-white font-medium">{comp.total.curr}</span>
                                {comp.total.diff !== 0 && (
                                  <span className={`text-xs font-medium ${comp.total.diff > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    ({comp.total.diff > 0 ? '+' : ''}{comp.total.diff})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              {comp.trend === 'improving' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                                  <TrendingDown className="w-3 h-3" />
                                  Baisse
                                </span>
                              ) : comp.trend === 'worsening' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                                  <TrendingUp className="w-3 h-3" />
                                  Hausse
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-500/20 text-gray-400 rounded text-xs">
                                  <Minus className="w-3 h-3" />
                                  Stable
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Tip */}
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-xs text-blue-400 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Programmez des scans reguliers pour un suivi optimal de l'evolution de votre posture de securite
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Documents & Reports Section */}
        <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
          <button
            onClick={() => toggleSection('documents')}
            className="w-full p-4 flex items-center justify-between hover:bg-brand-black/50"
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Rapports & documents
              {documents?.total_documents > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                  {documents.total_documents}
                </span>
              )}
            </h3>
            {expandedSections.documents ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {expandedSections.documents && (
            <div className="p-6 pt-0 border-t border-gray-800 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="text-center p-2 bg-brand-black rounded border border-gray-800">
                  <div className="text-xl font-bold text-orange-400">{scans?.filter(s => s.status === 'completed')?.length || 0}</div>
                  <div className="text-xs text-gray-500">Rapports de Scan</div>
                </div>
                <div className="text-center p-2 bg-brand-black rounded border border-gray-800">
                  <div className="text-xl font-bold text-cyan-400">1</div>
                  <div className="text-xs text-gray-500">Rapport GeoIP</div>
                </div>
                <div className="text-center p-2 bg-brand-black rounded border border-gray-800">
                  <div className="text-xl font-bold text-purple-400">{aiGeneratedReports?.length || 0}</div>
                  <div className="text-xs text-gray-500">Rapports IA</div>
                </div>
                <div className="text-center p-2 bg-brand-black rounded border border-gray-800">
                  <div className="text-xl font-bold text-green-400">{documents?.summary?.contracts || 0}</div>
                  <div className="text-xs text-gray-500">Contrats</div>
                </div>
                <div className="text-center p-2 bg-brand-black rounded border border-gray-800">
                  <div className="text-xl font-bold text-blue-400">{documents?.summary?.reports || 0}</div>
                  <div className="text-xs text-gray-500">Autres</div>
                </div>
              </div>

              {/* Scan Reports Section */}
              {scans?.filter(s => s.status === 'completed')?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-orange-400" />
                    Rapports de Scan ({scans.filter(s => s.status === 'completed').length})
                  </h4>
                  <div className="space-y-2">
                    {scans.filter(s => s.status === 'completed').map((scan) => (
                      <div
                        key={scan.id}
                        className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-orange-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-500/20">
                            <Target className="w-5 h-5 text-orange-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-xs">
                              Rapport - {scan.target}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="capitalize">{scan.scan_type || 'pentest'}</span>
                              <span>•</span>
                              <span>{new Date(scan.completed_at || scan.created_at).toLocaleDateString('fr-FR')}</span>
                              {scan.vulnerabilities_count > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="text-red-400">{scan.vulnerabilities_count} vulns</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleExportScanReport(scan.id, 'pdf')}
                            disabled={exportingScanId === `${scan.id}-pdf`}
                            className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                            title="Telecharger en PDF"
                          >
                            {exportingScanId === `${scan.id}-pdf` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <FileText className="w-3 h-3" />
                            )}
                            PDF
                          </button>
                          <button
                            onClick={() => handleExportScanReport(scan.id, 'docx')}
                            disabled={exportingScanId === `${scan.id}-docx`}
                            className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                            title="Telecharger en Word"
                          >
                            {exportingScanId === `${scan.id}-docx` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <FileText className="w-3 h-3" />
                            )}
                            DOCX
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GeoIP Report Section */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  Rapport Cartographie des Risques
                </h4>
                <div className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-cyan-500/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/20">
                      <Globe className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        Rapport GeoIP & Conformite
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Geolocalisation IPs</span>
                        <span>•</span>
                        <span>Risques pays</span>
                        <span>•</span>
                        <span>RGPD</span>
                        <span>•</span>
                        <span>Groupes de hackers</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleExportGeoIPReport}
                    disabled={exportingGeoIP}
                    className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-xs font-medium hover:bg-cyan-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                    title="Telecharger le rapport GeoIP"
                  >
                    {exportingGeoIP ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}
                    PDF
                  </button>
                </div>
              </div>

              {/* AI Generated Reports (Gamma) */}
              {aiGeneratedReports?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    Rapports IA Générés ({aiGeneratedReports.length})
                  </h4>
                  <div className="space-y-2">
                    {aiGeneratedReports.map((report) => (
                      <div
                        key={report.id}
                        className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-purple-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-purple-500/20">
                            <Sparkles className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-xs">
                              {report.title || report.report_type || 'Rapport IA'}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="capitalize">{report.report_type?.replace(/_/g, ' ') || 'rapport'}</span>
                              <span>•</span>
                              <span>{new Date(report.created_at).toLocaleDateString('fr-FR')}</span>
                              <span>•</span>
                              <span className={`${report.status === 'completed' ? 'text-green-400' : report.status === 'generating' ? 'text-yellow-400' : 'text-gray-400'}`}>
                                {report.status === 'completed' ? 'Pret' : report.status === 'generating' ? 'En cours...' : report.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {report.status === 'completed' && (
                            <>
                              <button
                                onClick={() => handleDownloadAiReport(report.id, 'pdf')}
                                disabled={downloadingReportId === `${report.id}-pdf`}
                                className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                                title="Telecharger en PDF"
                              >
                                {downloadingReportId === `${report.id}-pdf` ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <FileText className="w-3 h-3" />
                                )}
                                PDF
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDeleteReport(report.id)}
                            disabled={deletingReportId === report.id}
                            className="px-2 py-1.5 bg-gray-700/50 text-gray-400 rounded-lg text-xs font-medium hover:bg-red-500/20 hover:text-red-400 transition-colors flex items-center gap-1 disabled:opacity-50"
                            title="Supprimer le rapport"
                          >
                            {deletingReportId === report.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File-based Gamma Reports (fallback when no DB reports) */}
              {gammaFileReports?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                    Rapports de présentation ({gammaFileReports.length})
                  </h4>
                  <div className="space-y-2">
                    {gammaFileReports.map((report, idx) => (
                      <div
                        key={report.filename || idx}
                        className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-yellow-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-yellow-500/20">
                            <Sparkles className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-xs">
                              {report.filename || 'Rapport de présentation'}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="uppercase">{report.format || report.filename?.split('.').pop() || 'pptx'}</span>
                              {report.size && <span>{(report.size / 1024 / 1024).toFixed(1)} MB</span>}
                              <span>{new Date(report.created_at || report.modified_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadGammaFile(report.filename)}
                            disabled={downloadingReportId === `gamma-${report.filename}`}
                            className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg text-xs font-medium hover:bg-yellow-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                            title="Telecharger"
                          >
                            {downloadingReportId === `gamma-${report.filename}` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            Telecharger
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Documents */}
              {documents?.documents?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-400" />
                    Autres Documents ({documents.documents.length})
                  </h4>
                  <div className="space-y-2">
                    {documents.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-brand-green/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            doc.type === 'report' ? 'bg-blue-500/20' :
                            doc.type === 'contract' ? 'bg-green-500/20' :
                            doc.type === 'compliance' ? 'bg-purple-500/20' :
                            'bg-gray-500/20'
                          }`}>
                            <FileText className={`w-5 h-5 ${
                              doc.type === 'report' ? 'text-blue-400' :
                              doc.type === 'contract' ? 'text-green-400' :
                              doc.type === 'compliance' ? 'text-purple-400' :
                              'text-gray-400'
                            }`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-xs">{doc.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="uppercase">{doc.format}</span>
                              {doc.size && <span>{(doc.size / 1024).toFixed(1)} KB</span>}
                              <span>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                        </div>
                        <a
                          href={doc.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-brand-green hover:bg-brand-green/20 rounded-lg transition-colors"
                          title={t('workspaces.download')}
                        >
                          <Download className="w-5 h-5" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state only if no scans and no documents */}
              {(!scans || scans.filter(s => s.status === 'completed').length === 0) && (!documents?.documents || documents.documents.length === 0) && (
                <div className="bg-brand-black rounded-lg p-6 border border-gray-800 text-center">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-500 text-sm">
                    Aucun rapport de scan disponible
                  </p>
                  <p className="text-gray-600 text-xs mt-1">
                    Lancez des scans pour générer des rapports
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* VPN Credentials Modal */}
      {vpnCredentialsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-brand-green/20 rounded-xl">
                <Key className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{t('workspaces.vpnCredentials')}</h3>
                <p className="text-sm text-gray-400">
                  {vpnConfigDetails?.name || 'VPN'} {t('workspaces.requiresAuthentication')}
                </p>
              </div>
            </div>

            {/* Stored credentials indicator */}
            {hasStoredCredentials && (
              <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-400 font-medium">
                      {t('workspaces.storedCredentials')}
                    </span>
                    {storedCredentialsUsername && (
                      <span className="text-xs text-gray-400">
                        ({storedCredentialsUsername})
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleDeleteCredentials}
                    disabled={deletingCredentials}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
                    title={t('workspaces.deleteStoredCredentials')}
                  >
                    {deletingCredentials ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('workspaces.clickToUseCredentials')}
                </p>
              </div>
            )}

            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  {t('workspaces.username')}
                </label>
                <input
                  type="text"
                  value={vpnCredentials.username}
                  onChange={(e) => setVpnCredentials({ ...vpnCredentials, username: e.target.value })}
                  placeholder={hasStoredCredentials ? `(${t('workspaces.useStoredCredentials')})` : t('workspaces.enterVpnUsername')}
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/50 focus:border-brand-green"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  {t('workspaces.password')}
                </label>
                <input
                  type="password"
                  value={vpnCredentials.password}
                  onChange={(e) => setVpnCredentials({ ...vpnCredentials, password: e.target.value })}
                  placeholder={hasStoredCredentials ? `(${t('workspaces.useStoredCredentials')})` : t('workspaces.enterVpnPassword')}
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/50 focus:border-brand-green"
                />
              </div>

              {/* Save credentials checkbox */}
              {!hasStoredCredentials && (
                <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveCredentials}
                      onChange={(e) => setSaveCredentials(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-amber-500/50 bg-brand-black text-amber-500 focus:ring-amber-500/50 focus:ring-offset-0"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-amber-400">
                          {t('workspaces.saveCredentialsSecurely')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('workspaces.credentialsStorageInfo')}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setVpnCredentialsModal(false);
                    if (!hasStoredCredentials) {
                      setVpnCredentials({ username: '', password: '' });
                    }
                    setSaveCredentials(false);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  {t('workspaces.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={vpnConnecting || savingCredentials}
                  className="flex-1 px-4 py-3 bg-brand-green text-black rounded-lg hover:bg-brand-green-light transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {vpnConnecting || savingCredentials ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {savingCredentials ? t('workspaces.saving') : t('workspaces.connecting')}
                    </>
                  ) : (
                    <>
                      <Power className="w-4 h-4" />
                      {t('workspaces.connect')}
                    </>
                  )}
                </button>
              </div>
            </form>

            {vpnConfigDetails && (
              <div className="mt-4 p-3 bg-brand-black rounded-lg border border-gray-800">
                <p className="text-xs text-gray-500">
                  <span className="text-gray-400">{t('workspaces.server')}:</span> {vpnConfigDetails.remote_host}:{vpnConfigDetails.remote_port}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="text-gray-400">{t('workspaces.protocol')}:</span> {vpnConfigDetails.protocol?.toUpperCase() || 'UDP'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contract Status Dialog */}
      {showContractStatusDialog && latestContract && (
        <ContractStatusDialog
          contract={latestContract}
          onClose={() => setShowContractStatusDialog(false)}
          onRefresh={fetchLatestContract}
        />
      )}

      {/* Workspace Share Dialog */}
      <WorkspaceShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        workspace={workspace}
      />

      {/* VPN Configuration Wizard */}
      <VPNConfigWizard
        isOpen={showVpnWizard}
        onClose={() => setShowVpnWizard(false)}
        workspaceId={id}
        workspaceName={workspace?.name}
        onSuccess={() => {
          fetchWorkspace();  // Refresh workspace to get updated vpn_protocol
          fetchVpnStatus();
          setShowVpnWizard(false);
        }}
      />

      {/* Download Agent Modal */}
      <DownloadAgentModal
        isOpen={showDownloadAgentModal}
        onClose={() => setShowDownloadAgentModal(false)}
        onRegister={() => setShowRegisterAgentModal(true)}
        workspaceId={id}
      />

      {/* Register Agent Modal */}
      <RegisterAgentModal
        isOpen={showRegisterAgentModal}
        onClose={() => setShowRegisterAgentModal(false)}
        workspaceId={id}
        onSuccess={() => {
          setShowRegisterAgentModal(false);
          toast.success('Agent enregistré avec succès pour ce workspace !');
        }}
      />
    </div>
  );
};

export default WorkspaceDetails;
